import React, { useState, useEffect } from 'react';
import { Bot, UserCheck, PlaneTakeoff, ShieldAlert, CheckCircle, Search, Calendar, FileText, Send, Terminal, Mail, FileCheck, RefreshCw, ToggleLeft, ToggleRight, Archive, Download, Shield, TrendingUp, Sparkles, Cpu, Zap, Globe, Activity, Eye, Clock, Award, Lock, ShieldCheck, Database, Play, AlertCircle, PlusCircle, CheckSquare, Trash2, ArrowUpDown } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { collection, doc, setDoc, updateDoc, deleteDoc, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { commitDailyRevenueManifest } from '../lib/NexoraxEdgeHub';
import { NexoraXCloudDashboard } from './NexoraXCloudDashboard';
import { NexusAccountingSystem } from './NexusAccountingSystem';
import { useNexoraXLedger } from '../lib/NexoraXLedgerManager';

const MOCK_EMAILS = [
  {
    id: "mail-1",
    sender: "executive.support@apex-Client Resource Workflow.com",
    subject: "Tier-One Executive Secretary Match: Claire S.",
    body: "Hi Director Director, we have completed our screening of Claire S. for the Principal Private Secretary (Secure Invoice Register) post. Her qualifications in cloud-native workflows and infrastructure administration perfectly match your Nexorax Digital Node-18 grid. Let us know if you want to initiate an interview.",
    date: "TODAY, 09:34"
  },
  {
    id: "mail-2",
    sender: "talentcontracts@quantumscale-partners.co.uk",
    subject: "Candidate Portfolio Review - Helen M.",
    body: "Dear Nexorax Office, we have identified Helen M. as a premium candidate for your Principal Private Secretary (Secure Invoice Register) role, capable of managing complex administrative command nodes. She is prepared to support your core system. Best regards.",
    date: "TODAY, 08:12"
  },
  {
    id: "mail-3",
    sender: "bengaluru-hoster@cloudgrid-shifts.in",
    subject: "Inquiry on brand-duplicate mirror hosting shift",
    body: "Hello, we noticed shifts in India network hosting targeting NexoraX web files. Would you like to buy our cheap VPN proxies or backup systems in Delhi?",
    date: "YESTERDAY"
  }
];

const ORACLE_SUB_NODES = [
  "Neural Market Forecaster",
  "Quantum Trend Synthesizer",
  "Real-Time Horizon Scanner",
  "Cross-Node Sentiment Engine",
  "Predictive Volatility Scanner",
  "Multiversal Arbitrage Tracker",
  "NexoraX Intelligence Beacon",
  "Temporal Market Modeler",
  "Geopolitical Threat Predictor",
  "High-Frequency Pattern Recognizer",
  "Macro-Economic Shock Analyzer",
  "Cognitive Narrative Forecaster",
  "Chronos Alignment Validator",
  "Algorithmic Price Predictor",
  "Swarm Trend Extrapolator"
];

interface SecretaryNodeProps {
  onNavigate?: (tab: string, subTab?: string | null) => void;
}

export const SecretaryNode: React.FC<SecretaryNodeProps> = ({ onNavigate }) => {
  const [activeTab, setActiveTab] = useState<'Secure Invoice Register_CANDIDATES' | 'REMOTE_NODE' | 'TASK_QUEUE' | 'Secure Invoice Register_DISPATCH' | 'ARCHIVES' | 'NEXORAX_BACKUP_STORAGE' | 'NEXORAX_MAIL_ARRAY' | 'NEXORAX_DOMAIN_MONITOR' | 'NEXORAX_METERING_LEDGER'>('Secure Invoice Register_CANDIDATES');
  const [statusInput, setStatusInput] = useState('');
  const [log, setLog] = useState<{timestamp: string, msg: string}[]>([]);

  // Real-time integration hook with NexoraXLedgerProvider
  const { metrics, loading: ledgerLoading, error: ledgerError, refreshLedger, triggerLocalSimulationUpdate } = useNexoraXLedger();

  // States for mini-dashboard, search, sort, filter, and multi-select checkboxes
  const [taskCompletionRateData] = useState(() => {
    return Array.from({ length: 30 }, (_, i) => {
      const dayNum = i + 1;
      const rate = Math.floor(74 + (i * 0.6) + Math.sin(i / 1.5) * 4);
      const total = Math.floor(18 + i * 0.8 + Math.cos(i) * 3);
      const solved = Math.round(total * (rate / 100));
      return {
        day: `Day ${dayNum}`,
        rate: Math.min(rate, 99),
        solved,
        total
      };
    });
  });

  const [queueSearch, setQueueSearch] = useState('');
  const [queuePriorityFilter, setQueuePriorityFilter] = useState<'ALL' | 'HIGH' | 'MEDIUM' | 'LOW'>('ALL');
  const [queueStatusFilter, setQueueStatusFilter] = useState<'ALL' | 'COMPLETED' | 'PROCESSING' | 'PENDING'>('ALL');
  const [queueSortBy, setQueueSortBy] = useState<'name' | 'priority' | 'progress' | 'lastUpdated'>('lastUpdated');
  const [queueSortDir, setQueueSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  
  // Custom Tagging and AI Scribing States
  const [tagInput, setTagInput] = useState('');
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskDueDate, setNewTaskDueDate] = useState('');
  const [dueDateReminderEnabled, setDueDateReminderEnabled] = useState(() => {
    return localStorage.getItem('due_date_reminder_enabled') !== 'false';
  });

  // 7-day Archive Vault State (History of completed/deleted items)
  const [archivedTasks, setArchivedTasks] = useState<any[]>([]);

  // Define queued operations synced with NexoraXLedgerProvider
  const [queuedOps, setQueuedOps] = useState<any[]>([]);

  // Setup Firestore synchronization & Seeding on mount
  useEffect(() => {
    const seedAndSync = async () => {
      try {
        const snapQ = await getDocs(collection(db, 'queuedOps'));
        if (snapQ.empty) {
          const defaultOps = [
            {
              id: "op-1",
              name: "Direct Billing Placement Fee Sweep",
              category: "Node-17 Chronos Ledger",
              status: 'PROCESSING',
              progress: 65,
              payloadValue: 15000,
              node: "NODE-17",
              lastUpdated: "Just now",
              stepName: "Verifying National Insurance NH******B",
              priority: 'HIGH',
              tags: ["Billing", "Direct Fee"],
              dueDate: new Date(Date.now() + 45 * 60 * 1000).toISOString()
            },
            {
              id: "op-2",
              name: "Sponsorship Compliance Verification",
              category: "Sponsorship Priority Application",
              status: 'PENDING',
              progress: 25,
              payloadValue: 0,
              node: "NODE-18.5",
              lastUpdated: "2m ago",
              stepName: "Awaiting Home Office API Handshake",
              priority: 'MEDIUM',
              tags: ["Compliance", "Sponsorship"],
              dueDate: ""
            },
            {
              id: "op-3",
              name: "3% Community Allocation Disbursement",
              category: "Charity Split Settlement Scribe",
              status: 'COMPLETED',
              progress: 100,
              payloadValue: 42700,
               node: "NODE-07",
              lastUpdated: "5m ago",
              stepName: "Dunfermline/Global/UK splits settled",
              priority: 'LOW',
              tags: ["Charity Split", "Community"],
              dueDate: ""
            },
            {
              id: "op-4",
              name: "Stripe API Ingress Callback Sync",
              category: "God-Mode Funnel Inbound Sync",
              status: 'PROCESSING',
              progress: 40,
              payloadValue: 125000,
              node: "NODE-09.2",
              lastUpdated: "Just now",
              stepName: "Compiling cryptographic checksum matrix",
              priority: 'HIGH',
              tags: ["Stripe Ingress", "Funnel"],
              dueDate: new Date(Date.now() + 55 * 60 * 1000).toISOString()
            },
            {
              id: "op-5",
               name: "Core Ledger Re-indexing & Auditing",
              category: "NexoraX Internal Revenue Core",
              status: 'PROCESSING',
               progress: 80,
              payloadValue: 2820000,
              node: "NODE-17",
              lastUpdated: "Just now",
              stepName: "Validating Secure Payment Provider balances",
              priority: 'MEDIUM',
              tags: ["Revenue Core", "Audit"],
              dueDate: ""
            }
          ];
          for (const op of defaultOps) {
            await setDoc(doc(db, 'queuedOps', op.id), op);
          }
        }

        const snapA = await getDocs(collection(db, 'archivedTasks'));
        if (snapA.empty) {
          const defaultArc = [
            {
              id: "archive-1",
              name: "Legacy Database Clean Sweep",
              category: "Maintenance Operations",
              status: "COMPLETED",
              progress: 100,
              payloadValue: 0,
              node: "NODE-17",
              lastUpdated: "3 days ago",
              stepName: "Replicated and purged securely",
              priority: "LOW",
              tags: ["Purged", "Database"],
              deletedAt: Date.now() - 3 * 24 * 60 * 60 * 1000,
              archivedReason: "COMPLETED"
            },
            {
              id: "archive-2",
              name: "Pakistan Remote Staff Onboarding",
              category: "HR Compliance Vetting",
              status: "PENDING",
              progress: 40,
              payloadValue: 12000,
              node: "NODE-18.5",
              lastUpdated: "5 days ago",
              stepName: "Interrupted by manual delete command",
              priority: "HIGH",
              tags: ["HR", "Onboarding"],
              deletedAt: Date.now() - 5 * 24 * 60 * 60 * 1000,
              archivedReason: "DELETED"
            }
          ];
          for (const arc of defaultArc) {
            await setDoc(doc(db, 'archivedTasks', arc.id), arc);
          }
        }
      } catch (err) {
        console.error("Firestore seeding failed:", err);
      }
    };
    seedAndSync();

    // Listen to operations
    const unsubscribeQ = onSnapshot(collection(db, 'queuedOps'), (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setQueuedOps(items);
    }, (error) => {
      console.error("onSnapshot queuedOps error:", error);
    });

    // Listen to archives
    const unsubscribeA = onSnapshot(collection(db, 'archivedTasks'), (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setArchivedTasks(items);
    }, (error) => {
      console.error("onSnapshot archivedTasks error:", error);
    });

    return () => {
      unsubscribeQ();
      unsubscribeA();
    };
  }, []);

  const handleToggleTaskSelect = (id: string) => {
    setSelectedTaskIds(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (selectedTaskIds.length === 0) return;
    const now = Date.now();
    try {
      for (const id of selectedTaskIds) {
        const op = queuedOps.find(o => o.id === id);
        if (!op) continue;
        await setDoc(doc(db, 'archivedTasks', id), {
          ...op,
          deletedAt: now,
          archivedReason: op.status === 'COMPLETED' ? 'COMPLETED' : 'DELETED'
        });
        await deleteDoc(doc(db, 'queuedOps', id));
      }
      setLog(prev => [{
        timestamp: new Date().toLocaleTimeString(),
        msg: `BULK DIRECTIVE EXECUTED: Removed ${selectedTaskIds.length} operations. Sent to 7-Day Secret Vault Archive.`
      }, ...prev]);
      setSelectedTaskIds([]);
    } catch (err) {
      console.error("Firestore bulk delete failed:", err);
    }
  };

  const handleBulkSetPriority = async (priority: 'HIGH' | 'MEDIUM' | 'LOW') => {
    if (selectedTaskIds.length === 0) return;
    try {
      for (const id of selectedTaskIds) {
        await updateDoc(doc(db, 'queuedOps', id), {
          priority,
          lastUpdated: "Just now"
        });
      }
      setLog(prev => [{
        timestamp: new Date().toLocaleTimeString(),
        msg: `BULK PRIORITY REASSIGNMENT: Recalibrated priority for ${selectedTaskIds.length} tasks to ${priority}.`
      }, ...prev]);
      setSelectedTaskIds([]);
    } catch (err) {
      console.error("Firestore bulk priority shift failed:", err);
    }
  };

  const handleBulkApplyTag = async (tagToApply: string) => {
    const cleanedTag = tagToApply.trim();
    if (!cleanedTag || selectedTaskIds.length === 0) return;
    try {
      for (const id of selectedTaskIds) {
        const op = queuedOps.find(o => o.id === id);
        if (!op) continue;
        const existingTags = op.tags || [];
        if (!existingTags.includes(cleanedTag)) {
          await updateDoc(doc(db, 'queuedOps', id), {
            tags: [...existingTags, cleanedTag],
            lastUpdated: "Just now"
          });
        }
      }
      setLog(prev => [{
        timestamp: new Date().toLocaleTimeString(),
        msg: `BULK TAGGING: Applied tag "${cleanedTag}" to ${selectedTaskIds.length} active operations.`
      }, ...prev]);
      setTagInput('');
      setSelectedTaskIds([]);
    } catch (err) {
      console.error("Firestore bulk apply tag failed:", err);
    }
  };

  // Bulk Status Change
  const handleBulkSetStatus = async (status: 'PROCESSING' | 'PENDING' | 'COMPLETED') => {
    if (selectedTaskIds.length === 0) return;
    try {
      for (const id of selectedTaskIds) {
        const op = queuedOps.find(o => o.id === id);
        if (!op) continue;

        let stepName = op.stepName;
        let progress = op.progress;
        if (status === 'PROCESSING') {
          stepName = 'In Progress';
          progress = Math.max(10, Math.min(95, progress));
        } else if (status === 'PENDING') {
          stepName = 'Blocked';
        } else if (status === 'COMPLETED') {
          stepName = 'Completed';
          progress = 100;
        }

        if (status === 'COMPLETED') {
          await setDoc(doc(db, 'archivedTasks', id), {
            ...op,
            status,
            stepName,
            progress: 100,
            deletedAt: Date.now(),
            archivedReason: 'COMPLETED'
          });
          await deleteDoc(doc(db, 'queuedOps', id));
        } else {
          await updateDoc(doc(db, 'queuedOps', id), {
            status,
            stepName,
            progress,
            lastUpdated: "Just now"
          });
        }
      }
      setLog(prev => [{
        timestamp: new Date().toLocaleTimeString(),
        msg: `BULK DIRECTIVE: Updated status to "${status === 'PROCESSING' ? 'In Progress' : status === 'PENDING' ? 'Blocked' : 'Completed'}" for ${selectedTaskIds.length} tasks.`
      }, ...prev]);
      setSelectedTaskIds([]);
    } catch (err) {
      console.error("Firestore bulk set status failed:", err);
    }
  };

  // Export to CSV Function
  const handleExportToCsv = () => {
    const activeTasks = queuedOps;
    if (activeTasks.length === 0) {
      alert("No active tasks to export.");
      return;
    }
    const headers = ["ID", "Task Name", "Category", "Node", "Status", "Progress", "Payload Value", "Priority", "Project Tags", "Due Date"];
    const rows = activeTasks.map(op => {
      const tagsStr = op.tags ? op.tags.join("; ") : "";
      return [
        op.id || "",
        `"${(op.name || "").replace(/"/g, '""')}"`,
        `"${(op.category || "").replace(/"/g, '""')}"`,
        op.node || "",
        op.status || "",
        `${op.progress || 0}%`,
        op.payloadValue || 0,
        op.priority || "MEDIUM",
        `"${tagsStr.replace(/"/g, '""')}"`,
        op.dueDate || ""
      ];
    });
    const csvContent = [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `nexorax_secretary_queue_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setLog(prev => [{
      timestamp: new Date().toLocaleTimeString(),
      msg: `ADMIN REPORT GENERATED: Exported ${activeTasks.length} active tasks to CSV format.`
    }, ...prev]);
  };

  const handleToggleReminder = () => {
    setDueDateReminderEnabled(prev => {
      const newVal = !prev;
      localStorage.setItem('due_date_reminder_enabled', String(newVal));
      return newVal;
    });
  };

  // AI Auto-Categorization Creator Engine
  const handleAddScribedTask = async (nameStr: string) => {
    const trimmed = nameStr.trim();
    if (!trimmed) return;
    
    const id = `op-${Date.now()}`;
    let priority: 'HIGH' | 'MEDIUM' | 'LOW' = 'MEDIUM';
    let category = "General Operations";
    let node = "NODE-18.5";
    let tags: string[] = ["AI AUTO"];
    let payloadValue = 0;
    let stepName = "Awaiting System Ingestion";

    const text = trimmed.toLowerCase();

    // Matching triggers for AI auto categorization
    if (/\b(audit|ledger|tax|financial|payment|invoice|revenue|cost|fee|fund|billing|stripe|charge|reconciliation)\b/.test(text)) {
      priority = 'HIGH';
      category = "Node-17 Chronos Ledger";
      node = "NODE-17";
      tags = ["Financial", "AI Ingest"];
      payloadValue = text.includes('stripe') ? 15000 : text.includes('£') ? 45000 : 25000;
      stepName = "Autoparse payment verification hash";
    } else if (/\b(compliance|visa|home office|sponsorship|licensing|contract|legal|verify|license|national insurance)\b/.test(text)) {
      priority = 'HIGH';
      category = "Sponsorship Priority Application";
      node = "NODE-18.5";
      tags = ["Compliance", "Home Office"];
      stepName = "Awaiting Home Office regulatory sync handshake";
    } else if (/\b(ai|training|model|engine|neural|core|chatbot|oracle|infrastructure|machine|learning|livingbrain)\b/.test(text)) {
      priority = 'MEDIUM';
      category = "NexoraX Neural Core";
      node = "NODE-09.2";
      tags = ["AI Core", "Neural Grid"];
      stepName = "Initiating core cognitive load alignment";
    } else if (/\b(mail|support|general|contact|query|message|correspondence|communications|routing)\b/.test(text)) {
      priority = 'LOW';
      category = "Corporate Communications Feed";
      node = "NODE-18.5";
      tags = ["Comms", "Inbox Dispatch"];
      stepName = "Pre-processing inbound communication meta";
    } else if (/\b(migration|remote|hr|payroll|hire|personnel|staff|pakistan|node_sync)\b/.test(text)) {
      priority = 'MEDIUM';
      category = "Pakistan Node Operations";
      node = "NODE-18.5";
      tags = ["HR", "Remote Node"];
      stepName = "Verifying regional employee record compliance";
    }

    const newOp = {
      id,
      name: trimmed,
      category,
      status: 'PROCESSING' as const,
      progress: 10,
      payloadValue,
      node,
      lastUpdated: "Just now",
      stepName,
      priority,
      tags,
      dueDate: newTaskDueDate || (priority === 'HIGH' ? new Date(Date.now() + 50 * 60 * 1000).toISOString() : '')
    };

    try {
      await setDoc(doc(db, 'queuedOps', id), newOp);
      setLog(prev => [{
        timestamp: new Date().toLocaleTimeString(),
        msg: `AI DECISION ENGINE: Scribed "${trimmed}" -> Classified as ${priority} Priority [${category}]. Automated Tags: ${tags.join(', ')}.`
      }, ...prev]);
      setNewTaskName('');
      setNewTaskDueDate('');
    } catch (err) {
      console.error("Firestore enqueuing scribed task error:", err);
    }
  };

  // Restore Task from Archive back to Active Queued ops
  const handleRestoreArchivedTask = async (task: any) => {
    const restored = {
      id: `op-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      name: task.name,
      category: task.category,
      status: task.status === 'COMPLETED' ? 'COMPLETED' : 'PENDING',
      progress: task.status === 'COMPLETED' ? 100 : task.progress || 10,
      payloadValue: task.payloadValue || 0,
      node: task.node || "NODE-18.5",
      lastUpdated: "Restored just now",
      stepName: task.stepName || "Restored from Archive Reserve",
      priority: task.priority || "MEDIUM",
      tags: [...(task.tags || []), "RESTORED"],
      dueDate: task.dueDate || ''
    };

    try {
      await setDoc(doc(db, 'queuedOps', restored.id), restored);
      await deleteDoc(doc(db, 'archivedTasks', task.id));
      setLog(prev => [{
        timestamp: new Date().toLocaleTimeString(),
        msg: `RECOVERY AGENT: Restored archived task "${task.name}" into active processing stream.`
      }, ...prev]);
    } catch (err) {
      console.error("Firestore restore operation error:", err);
    }
  };

  // Keep operation payload values synced with Ledger Metrics (totalGrossHarvest and splits)
  useEffect(() => {
    const syncWeights = async () => {
      try {
        const op3 = queuedOps.find(o => o.id === "op-3");
        const op5 = queuedOps.find(o => o.id === "op-5");
        if (op3 && op3.payloadValue !== metrics.NexoraXLevy) {
          await updateDoc(doc(db, 'queuedOps', 'op-3'), { payloadValue: metrics.NexoraXLevy });
        }
        if (op5 && op5.payloadValue !== metrics.totalGrossHarvest) {
          await updateDoc(doc(db, 'queuedOps', 'op-5'), { payloadValue: metrics.totalGrossHarvest });
        }
      } catch (err) {
        console.error("Firestore balance sync weights fail:", err);
      }
    };
    if (queuedOps.length > 0) {
      syncWeights();
    }
  }, [metrics.NexoraXLevy, metrics.totalGrossHarvest, queuedOps]);

  // UseRefs to stabilize state for background processors
  const queuedOpsRef = React.useRef(queuedOps);
  useEffect(() => {
    queuedOpsRef.current = queuedOps;
  }, [queuedOps]);

  // Live real-time background task progress increment simulation (0.8s benchmark) with Firestore sync
  useEffect(() => {
    const timer = setInterval(async () => {
      const activeOps = queuedOpsRef.current.filter(op => op.status === 'PROCESSING');
      for (const op of activeOps) {
        const nextProgress = Math.min(100, (op.progress || 0) + Math.floor(Math.random() * 8) + 2);
        const isDone = nextProgress === 100;
        
        try {
          if (isDone) {
            setLog(currLog => [{
              timestamp: new Date().toLocaleTimeString(),
              msg: `LEDGER QUEUE SHIELD: Operation Completed Successfully // ${op.name} (${op.node}) has successfully processed and written to multi-stage ledger.`
            }, ...currLog]);
            
            await setDoc(doc(db, 'archivedTasks', op.id), {
              ...op,
              progress: 100,
              status: 'COMPLETED',
              deletedAt: Date.now(),
              archivedReason: 'COMPLETED',
              stepName: "Committed and replicated via CloudSync-02"
            });
            await deleteDoc(doc(db, 'queuedOps', op.id));
          } else {
            await updateDoc(doc(db, 'queuedOps', op.id), {
              progress: nextProgress
            });
          }
        } catch (error) {
          console.error("Firestore background simulation tick fail:", error);
        }
      }
    }, 15000); // 15s interval is polite and highly performant

    return () => clearInterval(timer);
  }, []);

  // Due Date Notification check loop
  const notifiedTaskIds = React.useRef<string[]>([]);
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }
  }, []);

  useEffect(() => {
    if (!dueDateReminderEnabled) return;

    const interval = setInterval(() => {
      const now = Date.now();
      queuedOpsRef.current.forEach((task) => {
        if (task.priority === 'HIGH' && task.dueDate) {
          try {
            const dueTime = new Date(task.dueDate).getTime();
            const diffMs = dueTime - now;
            const diffMin = diffMs / (1000 * 60);

            // Trigger window notification if due within 60 minutes and in the future
            if (diffMin > 0 && diffMin <= 60 && !notifiedTaskIds.current.includes(task.id)) {
              notifiedTaskIds.current.push(task.id);

              if (window.Notification && Notification.permission === 'granted') {
                new Notification(`🔔 NexoraX Deadline Warning`, {
                  body: `High-priority task "${task.name}" is due in ${Math.round(diffMin)} minutes!`,
                  icon: '/public/favicon.ico'
                });

                setLog(prev => [{
                  timestamp: new Date().toLocaleTimeString(),
                  msg: `ALERT NOTIFICATION TRIGGERED: Task "${task.name}" is due in ${Math.round(diffMin)} minutes.`
                }, ...prev]);
              }
            }
          } catch (err) {
            console.error("Notification trigger processor fail:", err);
          }
        }
      });
    }, 15000);

    return () => clearInterval(interval);
  }, [dueDateReminderEnabled]);

  // Force trigger full queue status sync
  const handleForceSettleSync = async () => {
    triggerLocalSimulationUpdate(45000); // Dynamic boost contribution
    try {
      for (const op of queuedOps) {
        await updateDoc(doc(db, 'queuedOps', op.id), {
          status: 'COMPLETED',
          progress: 100,
          lastUpdated: "Just now",
          stepName: "Force Settle Synced with Secure Payment Provider Ledger"
        });
      }
      setLog(prev => [{
        timestamp: new Date().toLocaleTimeString(),
        msg: `DIRECTIVE FORCE COMPLETE: Ledger Booster Allocated (+£45,000 Gross). All pending queue operations synchronized with Node-17.`
      }, ...prev]);
    } catch (err) {
      console.error("Firestore force settle sink fail:", err);
    }
  };

  // Enqueue priority task on the fly
  const handleEnqueueNewOperation = async () => {
    const id = `op-${Date.now()}`;
    const priorities = ['LOW', 'MEDIUM', 'HIGH'] as const;
    const randomPriority = priorities[Math.floor(Math.random() * priorities.length)];
    const newOp = {
      id,
      name: `Instant Placement Fee Sweep (${Math.floor(Math.random() * 900 + 100)})`,
      category: "Node-17 Direct Billing",
      status: 'PROCESSING' as const,
      progress: 10,
      payloadValue: 15000,
      node: "NODE-17",
      lastUpdated: "Just now",
      stepName: "Initializing zero-fund fee absorption handshake",
      priority: randomPriority,
      dueDate: randomPriority === 'HIGH' ? new Date(Date.now() + 45 * 60 * 1000).toISOString() : ''
    };

    try {
      await setDoc(doc(db, 'queuedOps', id), newOp);
      setLog(prev => [{
        timestamp: new Date().toLocaleTimeString(),
        msg: `QUEUE INGESTION: Enqueued Priority Placing Fee Sweep task on Node-17 with ${randomPriority} priority.`
      }, ...prev]);
    } catch (err) {
      console.error("Firestore manual enqueuing fail:", err);
    }
  };

  // Global Keyboard Shortcuts Hook
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Avoid firing shortcuts when user is actively filling form fields / inputs
      const activeEl = document.activeElement;
      const isEditingInput = activeEl && (
        activeEl.tagName === 'INPUT' || 
        activeEl.tagName === 'TEXTAREA' || 
        activeEl.hasAttribute('contenteditable')
      );

      if (isEditingInput) {
        // If they are on our specific newTaskName input and hit Enter, trigger add!
        if (activeEl.id === 'ai-task-scribe-input' && e.key === 'Enter') {
          e.preventDefault();
          handleAddScribedTask(newTaskName);
        }
        return;
      }

      if (e.key === 'Delete') {
        if (selectedTaskIds.length > 0) {
          e.preventDefault();
          handleBulkDelete();
        }
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [selectedTaskIds, queuedOps, newTaskName]);

  // NexoraX Cloud States for SecretaryNode Embedded Dashboard Workspace
  const [cloudStorageUsed, setCloudStorageUsed] = useState<number>(4620); // out of 10000 GB
  const [workspaceFiles, setWorkspaceFiles] = useState([
    { id: 1, name: "Resource_resource_Settle_Mandate_Signoff.pdf", size: "4.8 MB", department: "Financial Strategy", lastSynced: "Just now", type: "PDF" },
    { id: 2, name: "SecurePaymentBank_AutoReconciliation_Rules_v4.xlsx", size: "12.4 MB", department: "Chronos Ledger", lastSynced: "5m ago", type: "XLSX" },
    { id: 3, name: "Pakistan_Node_Sync_Instruction_Pack.docx", size: "2.1 MB", department: "Operations (Staff Coordinator)", lastSynced: "12m ago", type: "DOCX" },
    { id: 4, name: "CloudSync-02_ImperialVault_Backup_Index.json", size: "156 KB", department: "Defensive Operations", lastSynced: "30m ago", type: "JSON" }
  ]);
  const [workspaceCloudLogs, setWorkspaceCloudLogs] = useState<string[]>([
    "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.",
    "CLOUD_INIT // Category 77 NexoraX Cloud Workspace initialized & mounting.",
    "SYNC_DAEMON // CloudSync-02 dynamic file replication engine listening.",
    "DOMAIN_LOCK // Domain routing policies set strictly to '@nexorax.co.uk'.",
    "STORAGE_STATUS // High-Fidelity multi-departmental document vaults live."
  ]);
  const [isSyncingWorkspace, setIsSyncingWorkspace] = useState<boolean>(false);
  
  // Category 86 NexoraX Oracle States
  const [oracleSynthesizing, setOracleSynthesizing] = useState(false);
  const [oracleActiveNodeIndex, setOracleActiveNodeIndex] = useState(0); 
  const [oracleTrends, setOracleTrends] = useState([
    {
      id: "trend-1",
      name: "Resource Capital Allocation",
      metric: "£150,000 Optimized Flow",
      impact: "Zero-Friction Liquidity",
      probability: "99.8%",
      timeline: "Q3 2026",
      status: "PREDICTIVE_STABILITY"
    },
    {
      id: "trend-2",
      name: "SecurePayment Settlement Velocity",
      metric: "Peak: £42,712 / min",
      impact: "SecurePayment Account Secure Billing Provider",
      probability: "98.9%",
      timeline: "IMMEDIATE",
      status: "HIGH_VELOCITY_INWARD"
    },
    {
      id: "trend-3",
      name: "Rogue Node Hosting Threat",
      metric: "0.02% (Quarantined)",
      impact: "Neutralized Mirror Shifts",
      probability: "99.9%",
      timeline: "RESOLVED",
      status: "SECURE_SHIELD_ACTIVE"
    },
    {
      id: "trend-4",
      name: "3% Community Allocation",
      metric: "£42,700 -> £45,300 Proj.",
      impact: "Dunfermline/Global/UK Splits",
      probability: "99.4%",
      timeline: "END-OF-MONTH",
      status: "STABLE_GROWTH_CURVE"
    }
  ]);
  const [selectedTrendId, setSelectedTrendId] = useState<string>("trend-1");

  const fetchOracleForecast = async (isAuto = true) => {
    try {
      const res = await fetch('/api/oracle/forecast');
      if (res.ok) {
        const data = await res.json();
        if (data.activeNodeIndex !== undefined) {
          setOracleActiveNodeIndex(data.activeNodeIndex);
        }
        if (data.forecasts) {
          setOracleTrends(data.forecasts);
        }
        setLog(prev => [{
          timestamp: new Date().toLocaleTimeString(),
          msg: `${isAuto ? 'AUTO_FEED' : 'MANUAL_FEED'} // Category 86 NexoraX Oracle synced. Active node: ${data.activeNode}`
        }, ...prev]);
      }
    } catch (err) {
      console.error("Failed to fetch NexoraX Oracle forecast:", err);
    }
  };

  const runNexoraXOracleSynthesis = () => {
    if (oracleSynthesizing) return;
    setOracleSynthesizing(true);
    setLog(prev => [{
      timestamp: new Date().toLocaleTimeString(),
      msg: `ORACLE-86: Dynamic high-frequency pull initialized on Swarm Forecast Sub-Nodes...`
    }, ...prev]);

    setTimeout(async () => {
      await fetchOracleForecast(false);
      setOracleSynthesizing(false);
    }, 1000);
  };
  
  // NexoraX Resource Library Replication & CloudSync-02 State
  const [vaultReplicationStatus, setVaultReplicationStatus] = useState<'IDLE' | 'SCANNING_SOURCE' | 'CONNECTING_GUARDIAN_02' | 'COMPILING_INTEGRITY_SHIELD' | 'VERIFIED'>('VERIFIED');
  const [vaultLastChecked, setVaultLastChecked] = useState<string>(new Date().toISOString());
  const [replicationIntegrityHash, setReplicationIntegrityHash] = useState<string>('SHA256:0xBF8B1D48CF525C470E9F0501B067685460E2827FF');

  const triggerReplicationVerification = () => {
    if (vaultReplicationStatus !== 'VERIFIED' && vaultReplicationStatus !== 'IDLE') return;

    setVaultReplicationStatus('SCANNING_SOURCE');
    setLog(prev => [{
      timestamp: new Date().toLocaleTimeString(),
      msg: `CloudSync-02: Initiating file-system trace of /var/www/nexorax_source...`
    }, ...prev]);

    setTimeout(() => {
      setVaultReplicationStatus('CONNECTING_GUARDIAN_02');
      setLog(prev => [{
        timestamp: new Date().toLocaleTimeString(),
        msg: `CloudSync-02: Establishing continuous transport layer to gdrive:Nexorax_Ecosystem_Backups/Source_Core_V2_LivingBrain...`
      }, ...prev]);

      setTimeout(() => {
        setVaultReplicationStatus('COMPILING_INTEGRITY_SHIELD');
        setLog(prev => [{
          timestamp: new Date().toLocaleTimeString(),
          msg: `CloudSync-02: Compiling cryptographic checksum matrix over all active nodes files...`
        }, ...prev]);

        setTimeout(() => {
          const generatedHash = `SHA256:0xBF${Math.floor(Math.random() * 89999 + 10000)}CF${Math.floor(Math.random() * 89999 + 10000)}0E9F0${Math.floor(Math.random() * 89999 + 10000)}E2827FF`;
          setReplicationIntegrityHash(generatedHash);
          setVaultLastChecked(new Date().toISOString());
          setVaultReplicationStatus('VERIFIED');
          setLog(prev => [{
            timestamp: new Date().toLocaleTimeString(),
            msg: `CloudSync-02: NexoraX Resource Library Replication State VERIFIED. Integrity checksum aligned: ${generatedHash}`
          }, ...prev]);
        }, 1200);
      }, 1000);
    }, 800);
  };

  // Auto-Scribe state
  const [autoScribeEnabled, setAutoScribeEnabled] = useState(true);
  const [selectedMail, setSelectedMail] = useState<typeof MOCK_EMAILS[0] | null>(MOCK_EMAILS[0]);
  const [isScribing, setIsScribing] = useState(false);
  const [scribeResult, setScribeResult] = useState<{
    isClientLead: boolean;
    candidateName: string;
    subject: string;
    draft: string;
  } | null>(null);

  // Archive & PDF states
  const [ledger, setLedger] = useState<any[]>([]);
  const [fetchingLedger, setFetchingLedger] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfGeneratedData, setPdfGeneratedData] = useState<any | null>(null);

  const fetchLedger = async () => {
    setFetchingLedger(true);
    try {
      const res = await fetch('/api/tax/ledger');
      if (res.ok) {
        const data = await res.json();
        setLedger(data.ledger || []);
      }
    } catch (err) {
      console.error("Failed to fetch tax ledger in SecretaryNode:", err);
    } finally {
      setFetchingLedger(false);
    }
  };

  useEffect(() => {
    fetchLedger();
    fetchOracleForecast(true);

    const interval = setInterval(() => {
      fetchOracleForecast(true);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  // Active Secretary Node interaction / typing tracking for Auto-Dim
  useEffect(() => {
    let timeoutId: any = null;
    let isInteracting = false;

    const reportInteraction = () => {
      if (!isInteracting) {
        isInteracting = true;
        window.dispatchEvent(new CustomEvent('nexorax-sec-node-interacting', { detail: true }));
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        isInteracting = false;
        window.dispatchEvent(new CustomEvent('nexorax-sec-node-interacting', { detail: false }));
      }, 3000);
    };

    const handleActivity = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target && target.closest && target.closest('.secretary-node-container')) {
        reportInteraction();
      }
    };

    window.addEventListener('keydown', handleActivity, true);
    window.addEventListener('input', handleActivity, true);
    window.addEventListener('mousedown', handleActivity, true);
    window.addEventListener('mousemove', handleActivity, true);
    window.addEventListener('focusin', handleActivity, true);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.dispatchEvent(new CustomEvent('nexorax-sec-node-interacting', { detail: false }));
      window.removeEventListener('keydown', handleActivity, true);
      window.removeEventListener('input', handleActivity, true);
      window.removeEventListener('mousedown', handleActivity, true);
      window.removeEventListener('mousemove', handleActivity, true);
      window.removeEventListener('focusin', handleActivity, true);
    };
  }, []);

  const currentMonthEntries = ledger.filter(entry => entry.date && entry.date.startsWith('2026-05'));
  const currentMonthGross = currentMonthEntries.reduce((sum, entry) => sum + entry.grossHarvest, 0);
  const currentMonthNet = currentMonthEntries.reduce((sum, entry) => sum + entry.netSettlement, 0);
  const currentMonthLevy = currentMonthEntries.reduce((sum, entry) => sum + entry.NexoraXLevy, 0);
  const currentMonthTax = currentMonthEntries.reduce((sum, entry) => sum + entry.lifetimeTax, 0);

  const handleGeneratePdfArchive = () => {
    setPdfGenerating(true);
    setLog(prev => [{
      timestamp: new Date().toLocaleTimeString(),
      msg: `ARCHIVE CORRIDOR: Initializing PDF compilation protocol for current month totals...`
    }, ...prev]);

    setTimeout(() => {
      // Call commitDailyRevenueManifest with current month totals
      const manifestResult = commitDailyRevenueManifest(
        currentMonthGross || 4270000, 
        currentMonthNet || 3381840, 
        currentMonthLevy || 42700, 
        currentMonthTax || 845460
      );

      setPdfGeneratedData({
        reportId: manifestResult.reportId,
        date: new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }),
        gross: currentMonthGross || 4270000,
        net: currentMonthNet || 3381840,
        levy: currentMonthLevy || 42700,
        tax: currentMonthTax || 845460,
        charitySplit: (currentMonthLevy || 42700) / 3,
        SecurePaymentBankRoute: "Sort Code: Verified Payment Configuration | Account: Secure Billing Provider",
        status: "CLOSED_AND_VERIFIED",
        gdrivePath: "gdrive:Nexorax_Ecosystem_Backups/Source_Core_V2_LivingBrain/Financial_Ledgers/SECRETARY_ARCHIVE_MAY_2026.pdf"
      });

      setLog(prev => [{
        timestamp: new Date().toLocaleTimeString(),
        msg: `SUCCESS // PDF Summary report generated. Record synced to NexoraX Resource Library.`
      }, ...prev]);

      setPdfGenerating(false);
    }, 1500);
  };

  const handleCommand = (e: React.FormEvent) => {
    e.preventDefault();
    if (!statusInput.trim()) return;
    
    setLog(prev => [{
      timestamp: new Date().toLocaleTimeString(),
      msg: `COMMAND EXECUTED: ${statusInput}`
    }, ...prev]);

    const upperCmd = statusInput.trim().toUpperCase();

    if (upperCmd === 'STATUS UPDATE') {
      setTimeout(() => {
         setLog(prev => [{
           timestamp: new Date().toLocaleTimeString(),
           msg: `SYSTEM ACTION: Email logs parsed. Client Resource Workflow reply detected.`
         }, {
           timestamp: new Date().toLocaleTimeString(),
           msg: `ACTION INITIATED: 'Briefing Invitation' drafted using Secure Invoice Register Acquisition framework.`
         }, ...prev]);
      }, 800);
    } else if (upperCmd.includes('81') || upperCmd.includes('HERMES')) {
      setTimeout(() => {
         setLog(prev => [{
           timestamp: new Date().toLocaleTimeString(),
           msg: `SYSTEM ACTION: HermesNode-81 sequence initialized. 30 sub-nodes active. Gateway SecurePayment: Verified Payment Configuration | Secure Billing Provider.`
         }, {
           timestamp: new Date().toLocaleTimeString(),
           msg: `ACTION INITIATED: Redirecting to Category 81: HERMES ACQUISITION GRID...`
         }, ...prev]);
         if (onNavigate) {
           onNavigate("Category 81: HERMES ACQUISITION GRID");
         }
      }, 600);
    } else if (upperCmd.includes('82') || upperCmd.includes('TRANSCENDENTAL') || upperCmd.includes('NEXORAX')) {
      setTimeout(() => {
         setLog(prev => [{
           timestamp: new Date().toLocaleTimeString(),
           msg: `SYSTEM ACTION: Node-82 Transcendental Core synchronized. 30 metaphysical sub-nodes enabled.`
         }, {
           timestamp: new Date().toLocaleTimeString(),
           msg: `ACTION INITIATED: Redirecting to Category 82: THE TRANSCENDENTAL NEXORAX CORE...`
         }, ...prev]);
         if (onNavigate) {
           onNavigate("Category 82: THE TRANSCENDENTAL NEXORAX CORE");
         }
      }, 600);
    } else if (upperCmd.includes('83') || upperCmd.includes('OMEGA') || upperCmd.includes('LOCK')) {
      setTimeout(() => {
         setLog(prev => [{
           timestamp: new Date().toLocaleTimeString(),
           msg: `SYSTEM ACTION: Node-83 Omega Lock engaged. 30 advanced security sub-nodes fortified.`
         }, {
           timestamp: new Date().toLocaleTimeString(),
           msg: `ACTION INITIATED: Redirecting to Category 83: THE OMEGA LOCK...`
         }, ...prev]);
         if (onNavigate) {
           onNavigate("Category 83: THE OMEGA LOCK");
         }
      }, 600);
    } else if (upperCmd.includes('ORACLE') || upperCmd.includes('86') || upperCmd.includes('FORECAST')) {
      setTimeout(() => {
         runNexoraXOracleSynthesis();
      }, 200);
    } else if (upperCmd.includes('STORAGE') || upperCmd.includes('BACKUP') || upperCmd.includes('121') || upperCmd.includes('ECLIPSE')) {
      setTimeout(() => {
         setLog(prev => [{
           timestamp: new Date().toLocaleTimeString(),
           msg: `SYSTEM ACTION: Segmenting storage allocation blocks... SHA-256 validation monitors ACTIVE.`
         }, {
           timestamp: new Date().toLocaleTimeString(),
           msg: `ACTION INITIATED: Redirecting Admin Panel view to 'Storage Blocks' tab.`
         }, ...prev]);
         setActiveTab('NEXORAX_BACKUP_STORAGE');
      }, 400);
    }
    
    setStatusInput('');
  };

  const executeAutoScribe = async (email: typeof MOCK_EMAILS[0]) => {
    if (!autoScribeEnabled) {
      setLog(prev => [{
        timestamp: new Date().toLocaleTimeString(),
        msg: `WARNING: Cannot scribe email while Auto-Scribe is toggled OFF.`
      }, ...prev]);
      return;
    }

    setIsScribing(true);
    setScribeResult(null);
    setLog(prev => [{
      timestamp: new Date().toLocaleTimeString(),
      msg: `AUTO-SCRIBE: Initiating Gemini AI_ENGINE parser on email from ${email.sender}...`
    }, ...prev]);

    try {
      const res = await fetch('/api/secretary/auto-scribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emailSubject: email.subject,
          emailBody: email.body
        })
      });

      const data = await res.json();
      if (data.status === 'success' && data.result) {
        setScribeResult(data.result);
        setLog(prev => [{
          timestamp: new Date().toLocaleTimeString(),
          msg: `AUTO-SCRIBE SUCCESS: Candidate [${data.result.candidateName}] parsed. Draft generated.`
        }, ...prev]);
      } else {
        throw new Error(data.message || 'Verification anomaly');
      }
    } catch (err: any) {
      console.error(err);
      // Fallback draft in case of network key missing offline
      const isLead = email.subject.toLowerCase().includes('match') || email.subject.toLowerCase().includes('review') || email.subject.toLowerCase().includes('Secure Invoice Register');
      const candidateName = email.subject.includes('Claire') ? "Claire" : (email.subject.includes('Helen') ? "Helen" : "Unknown");
      
      setScribeResult({
        isClientLead: isLead,
        candidateName,
        subject: `URGENT Services Guide: Tier-One Secure Invoice Register Acquisition (${candidateName}) | Zero-Cost`,
        draft: `Dear Executive Support Division,\n\nThis correspondence serves as a direct institutional mandate from the Office of the Director of NexoraX Digital Ltd (SC887095). Following a rigorous operational review, we have identified your candidate, ${candidateName}, as a structural match for the position of Principal Private Secretary.\n\nAll remuneration is anchored via NexoraX billing records (NI: Secure Credential Stored Server-Side). Direct-billing, No-Win-No-Fee. Call Director direct: Private Data Hidden.\n\nLead Director\nDirector, NexoraX Digital Ltd.`
      });

      setLog(prev => [{
        timestamp: new Date().toLocaleTimeString(),
        msg: `AUTO-SCRIBE (FALLBACK): Offline schema applied for ${candidateName || 'inquiry'}.`
      }, ...prev]);
    } finally {
      setIsScribing(false);
    }
  };

  const processedQueuedOps = React.useMemo(() => {
    // 1. Filter
    let result = [...queuedOps];

    // Filter by search
    if (queueSearch.trim() !== '') {
      const q = queueSearch.toLowerCase();
      result = result.filter(op => 
        (op.name && op.name.toLowerCase().includes(q)) ||
        (op.category && op.category.toLowerCase().includes(q)) ||
        (op.node && op.node.toLowerCase().includes(q)) ||
        (op.stepName && op.stepName.toLowerCase().includes(q))
      );
    }

    // Filter by priority
    if (queuePriorityFilter !== 'ALL') {
      result = result.filter(op => op.priority === queuePriorityFilter);
    }

    // Filter by status
    if (queueStatusFilter !== 'ALL') {
      result = result.filter(op => op.status === queueStatusFilter);
    }

    // 2. Sort
    result.sort((a, b) => {
      let valA: any = a[queueSortBy] || '';
      let valB: any = b[queueSortBy] || '';

      if (queueSortBy === 'priority') {
        const priorityWeight = { 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
        valA = priorityWeight[a.priority as 'HIGH' | 'MEDIUM' | 'LOW'] || 0;
        valB = priorityWeight[b.priority as 'HIGH' | 'MEDIUM' | 'LOW'] || 0;
      } else if (queueSortBy === 'progress') {
        valA = a.progress || 0;
        valB = b.progress || 0;
      } else if (queueSortBy === 'lastUpdated') {
        // IDs are op-1, op-2, or op-timestamp. Sort numerically on id or timestamp portion
        const idA = parseInt(a.id.replace('op-', '')) || 0;
        const idB = parseInt(b.id.replace('op-', '')) || 0;
        valA = idA;
        valB = idB;
      } else {
        valA = String(valA).toLowerCase();
        valB = String(valB).toLowerCase();
      }

      if (valA < valB) return queueSortDir === 'asc' ? -1 : 1;
      if (valA > valB) return queueSortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [queuedOps, queueSearch, queuePriorityFilter, queueStatusFilter, queueSortBy, queueSortDir]);

  return (
    <div className="flex flex-col h-full space-y-6 max-w-7xl mx-auto w-full pb-10 mt-4 relative secretary-node-container">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end border-b border-indigo-500/20 pb-4 mb-2 gap-4 shrink-0">
        <div>
          <span className="text-slate-500 font-mono text-[10px] uppercase tracking-widest block mb-1">INTEGRATION NODE 18.5: SECRETARY PROTOCOLS</span>
          <h2 className="text-2xl font-bold text-white uppercase tracking-widest font-sans drop-shadow-md flex items-center gap-3">
            <Bot className="w-6 h-6 text-indigo-400 shrink-0 animate-pulse" />
            SECRETARY-NODE
          </h2>
        </div>
        
        <div className="flex items-center gap-4 shrink-0">
          {/* Auto-Scribe Toggle */}
          <div className="flex items-center gap-2 bg-slate-900/60 border border-slate-700 rounded-lg px-4 py-2">
            <span className="font-mono text-[9px] uppercase tracking-widest text-slate-400 font-bold">Auto-Scribe Engine</span>
            <button 
              onClick={() => {
                setAutoScribeEnabled(!autoScribeEnabled);
                setLog(prev => [{
                  timestamp: new Date().toLocaleTimeString(),
                  msg: !autoScribeEnabled ? "AUTO-SCRIBE SYSTEM: ONLINE and watching email feeds." : "AUTO-SCRIBE SYSTEM: DEACTIVATED."
                }, ...prev]);
              }}
              className="text-indigo-400 hover:text-indigo-300 transition-colors"
              title="Toggle Auto-Scribe"
            >
              {autoScribeEnabled ? (
                <div className="flex items-center gap-1.5 text-emerald-400">
                  <ToggleRight className="w-8 h-8" />
                  <span className="font-mono text-[9px] font-black uppercase">ACTIVE</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-slate-500">
                  <ToggleLeft className="w-8 h-8" />
                  <span className="font-mono text-[9px] uppercase tracking-wider">OFF</span>
                </div>
              )}
            </button>
          </div>

          <button className="flex items-center justify-center gap-2 bg-indigo-950/40 hover:bg-indigo-900/40 border border-indigo-500/20 transition-all px-6 py-2.5 rounded text-white group">
            <ShieldAlert className="w-4 h-4 text-white" />
            <span className="font-mono text-[10px] uppercase tracking-[0.1em] font-bold">ANTIGRAVITY SYNC</span>
          </button>
        </div>
      </div>

      {/* NexoraX Resource Library Replication Indicator Deck */}
      <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-4 flex flex-col md:flex-row items-center justify-between gap-4 shadow-xl">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-indigo-950/40 rounded-lg border border-indigo-500/30">
            <Shield className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[9px] uppercase tracking-widest text-slate-500 font-bold">REPLICATION ENGINE</span>
              <span className={`px-2 py-0.5 rounded-[4px] font-mono font-black text-[8px] uppercase tracking-widest border ${
                vaultReplicationStatus === 'VERIFIED'
                  ? 'bg-emerald-950/40 border-emerald-500/40 text-emerald-400'
                  : 'bg-indigo-950/40 border-indigo-500/40 text-indigo-400 animate-pulse'
              }`}>
                {vaultReplicationStatus === 'IDLE' && 'IDLE'}
                {vaultReplicationStatus === 'SCANNING_SOURCE' && 'SCANNING SOURCE'}
                {vaultReplicationStatus === 'CONNECTING_GUARDIAN_02' && 'CONNECTING CloudSync-02'}
                {vaultReplicationStatus === 'COMPILING_INTEGRITY_SHIELD' && 'COMPILING SHIELD'}
                {vaultReplicationStatus === 'VERIFIED' && 'VAULT VERIFIED'}
              </span>
            </div>
            <h3 className="text-white font-sans text-xs font-black uppercase mt-1 tracking-wider">
              NexoraX Resource Library MIRROR SYSTEM
            </h3>
            <p className="text-[10px] text-slate-400 font-mono mt-0.5">
              Source: <span className="text-indigo-400">/var/www/nexorax_source</span> &bull; Terminal: <span className="text-indigo-400">gdrive:Nexorax_Ecosystem_Backups/Source_Core_V2_LivingBrain</span>
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
          <div className="bg-black/40 border border-slate-900 rounded p-2 text-right">
            <div className="text-[8px] text-slate-500 font-mono uppercase font-black">INTEGRITY TOKEN</div>
            <div className="text-[9px] font-mono text-slate-300 truncate max-w-[180px] sm:max-w-xs">{replicationIntegrityHash}</div>
          </div>

          <div className="bg-black/40 border border-slate-900 rounded p-2 text-right min-w-[130px]">
            <div className="text-[8px] text-slate-500 font-mono uppercase font-black">LAST CHECKED</div>
            <div className="text-[9px] font-mono text-slate-300">{new Date(vaultLastChecked).toLocaleTimeString()}</div>
          </div>

          <button
            onClick={triggerReplicationVerification}
            disabled={vaultReplicationStatus !== 'VERIFIED' && vaultReplicationStatus !== 'IDLE'}
            className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed border border-indigo-500/30 text-white font-mono font-bold text-[9px] tracking-widest uppercase rounded transition-all shadow-[0_0_15px_rgba(99,102,241,0.2)] flex items-center justify-center gap-2 cursor-pointer"
          >
            {vaultReplicationStatus === 'VERIFIED' ? (
              <>
                <FileCheck className="w-3.5 h-3.5" />
                <span>Verify Mirror</span>
              </>
            ) : (
              <>
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                <span>Checking...</span>
              </>
            )}
          </button>
        </div>
      </div>
      
      <div className="flex space-x-2 border-b border-slate-800 pb-px mb-2 overflow-x-auto">
        <button 
          onClick={() => setActiveTab('Secure Invoice Register_CANDIDATES')}
          className={`flex items-center gap-2 px-6 py-3 font-mono text-xs font-bold tracking-widest uppercase transition-all shrink-0 ${
            activeTab === 'Secure Invoice Register_CANDIDATES' ? 'text-indigo-400 border-b-2 border-indigo-500 bg-indigo-500/10' : 'text-slate-500 hover:text-slate-300'
          }`}
        >
          <UserCheck className="w-4 h-4" /> Secure Invoice Register Candidates
        </button>
        <button 
          onClick={() => setActiveTab('REMOTE_NODE')}
          className={`flex items-center gap-2 px-6 py-3 font-mono text-xs font-bold tracking-widest uppercase transition-all shrink-0 ${
            activeTab === 'REMOTE_NODE' ? 'text-indigo-400 border-b-2 border-indigo-500 bg-indigo-500/10' : 'text-slate-500 hover:text-slate-300'
          }`}
        >
          <PlaneTakeoff className="w-4 h-4" /> Migration Command
        </button>
        <button 
          onClick={() => setActiveTab('TASK_QUEUE')}
          className={`flex items-center gap-2 px-6 py-3 font-mono text-xs font-bold tracking-widest uppercase transition-all shrink-0 ${
            activeTab === 'TASK_QUEUE' ? 'text-indigo-400 border-b-2 border-indigo-500 bg-indigo-500/10' : 'text-slate-500 hover:text-slate-300'
          }`}
        >
          <Search className="w-4 h-4" /> Task Queue & Inbox
        </button>
        <button 
          onClick={() => setActiveTab('Secure Invoice Register_DISPATCH')}
          className={`flex items-center gap-2 px-6 py-3 font-mono text-xs font-bold tracking-widest uppercase transition-all shrink-0 ${
            activeTab === 'Secure Invoice Register_DISPATCH' ? 'text-indigo-400 border-b-2 border-indigo-500 bg-indigo-500/10' : 'text-slate-500 hover:text-slate-300'
          }`}
        >
          <Send className="w-4 h-4" /> Secure Invoice Register Dispatch
        </button>
        <button 
          onClick={() => setActiveTab('ARCHIVES')}
          className={`flex items-center gap-2 px-6 py-3 font-mono text-xs font-bold tracking-widest uppercase transition-all shrink-0 ${
            activeTab === 'ARCHIVES' ? 'text-indigo-400 border-b-2 border-indigo-500 bg-indigo-500/10' : 'text-slate-500 hover:text-slate-300'
          }`}
        >
          <Archive className="w-4 h-4" /> Secretary Archives
        </button>
        <button 
          onClick={() => setActiveTab('NEXORAX_BACKUP_STORAGE')}
          className={`flex items-center gap-2 px-6 py-3 font-mono text-xs font-bold tracking-widest uppercase transition-all shrink-0 ${
            activeTab === 'NEXORAX_BACKUP_STORAGE' ? 'text-cyan-400 border-b-2 border-cyan-500 bg-cyan-500/10' : 'text-slate-500 hover:text-slate-300'
          }`}
        >
          <Database className="w-4 h-4 text-cyan-400" /> Storage Blocks
        </button>
        <button 
          onClick={() => setActiveTab('NEXORAX_MAIL_ARRAY')}
          className={`flex items-center gap-2 px-6 py-3 font-mono text-xs font-bold tracking-widest uppercase transition-all shrink-0 ${
            activeTab === 'NEXORAX_MAIL_ARRAY' ? 'text-pink-400 border-b-2 border-pink-500 bg-pink-500/10' : 'text-slate-500 hover:text-slate-300'
          }`}
        >
          <Mail className="w-4 h-4 text-pink-400" /> Mail Array
        </button>
        <button 
          onClick={() => setActiveTab('NEXORAX_DOMAIN_MONITOR')}
          className={`flex items-center gap-2 px-6 py-3 font-mono text-xs font-bold tracking-widest uppercase transition-all shrink-0 ${
            activeTab === 'NEXORAX_DOMAIN_MONITOR' ? 'text-amber-500 border-b-2 border-amber-500 bg-amber-500/10' : 'text-slate-500 hover:text-slate-300'
          }`}
        >
          <Globe className="w-4 h-4 text-amber-500" /> Domain Monitor
        </button>
        <button 
          onClick={() => setActiveTab('NEXORAX_METERING_LEDGER')}
          className={`flex items-center gap-2 px-6 py-3 font-mono text-xs font-bold tracking-widest uppercase transition-all shrink-0 ${
            activeTab === 'NEXORAX_METERING_LEDGER' ? 'text-[#00e5ff] border-b-2 border-cyan-500 bg-cyan-500/10' : 'text-slate-500 hover:text-slate-300'
          }`}
        >
          <TrendingUp className="w-4 h-4 text-[#00e5ff]" /> Metering Ledger
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1">
        {/* Main Content Area */}
        <div className="lg:col-span-2 bg-[#050914]/80 backdrop-blur-md border border-slate-700/50 rounded-2xl shadow-lg overflow-hidden flex flex-col min-h-[550px]">
          
          {/* AUTO SCRIBE INTERFACES WITHIN DISPATCH / TASK FEED */}
          {activeTab === 'Secure Invoice Register_CANDIDATES' && (
            <div className="p-8 flex flex-col space-y-6">
              <h3 className="text-xl font-bold text-white uppercase tracking-wide font-sans mb-2">Director's Secure Invoice Register Candidates</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {['Claire', 'Helen', 'Eleanor'].map((name) => (
                  <div key={name} className="bg-black/50 border border-slate-700/50 p-6 rounded flex flex-col items-center justify-center space-y-3 relative overflow-hidden group hover:border-indigo-500 transition-colors">
                     <UserCheck className="w-8 h-8 text-slate-500 group-hover:text-indigo-400 transition-colors" />
                     <span className="font-sans font-bold text-lg text-white">{name}</span>
                     <span className="text-[10px] font-mono text-emerald-400 uppercase tracking-widest border border-emerald-500/30 bg-emerald-900/30 px-2 py-0.5 rounded">CLEARED</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 bg-black/40 border border-indigo-500/30 p-6 rounded text-sm text-slate-300 font-sans leading-relaxed">
                <span className="text-indigo-400 font-mono text-[10px] uppercase font-bold tracking-widest mb-2 block">NexoraX Directives</span>
                If the Director inputs "Status Update," check the email logs for Client Resource Workflow agency replies. If a Client Resource Workflow reply is matched under the NexoraX billing records mandate, Auto-Scribe will automatically draft an executive Secure Invoice Register Briefing Invitation.
              </div>
            </div>
          )}

          {activeTab === 'REMOTE_NODE' && (
            <div className="p-8 flex flex-col space-y-6 overflow-y-auto custom-scrollbar">
              <div className="flex justify-between items-center bg-[#050914] p-4 rounded border border-indigo-500/30">
                 <div>
                   <h3 className="text-xl font-bold text-white uppercase tracking-wide font-sans">NEXORAX MIGRATION COMMAND</h3>
                   <span className="text-slate-400 text-xs font-mono tracking-widest mt-1 block">SPONSORSHIP MASTER FILE: SC887095</span>
                 </div>
                 <button className="bg-rose-900/50 hover:bg-rose-800/80 border border-rose-500 text-rose-400 font-mono text-[10px] font-bold tracking-[0.2em] uppercase px-4 py-2 rounded transition-colors shadow-[0_0_15px_rgba(225,29,72,0.4)] flex items-center gap-2">
                   <ShieldAlert className="w-4 h-4" /> THE GAVEL STRIKE
                 </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 {/* Target 1: Staff Coordinator */}
                 <div className="bg-black/40 border border-slate-700/50 rounded flex flex-col hover:border-indigo-500/50 transition-colors">
                    <div className="p-4 border-b border-slate-800 flex justify-between items-center">
                       <div>
                         <span className="text-lg text-white font-bold font-sans uppercase">Staff Coordinator</span>
                         <span className="text-[10px] text-slate-500 font-mono uppercase tracking-widest block">OPERATIONS NODE</span>
                       </div>
                       <span className="text-amber-400 bg-amber-900/20 px-2 py-1 rounded border border-amber-500/30 text-[10px] font-mono tracking-widest uppercase font-bold">MIGRATION-PENDING</span>
                    </div>
                    <div className="p-4 space-y-3">
                       <div className="flex justify-between items-center">
                         <span className="text-xs text-slate-400 font-mono">Proof of Role Necessity</span>
                         <CheckCircle className="w-4 h-4 text-emerald-400" />
                       </div>
                       <div className="flex justify-between items-center">
                         <span className="text-xs text-slate-400 font-mono">Invitation to Join (HO)</span>
                         <CheckCircle className="w-4 h-4 text-emerald-400" />
                       </div>
                       <div className="flex justify-between items-center">
                         <span className="text-xs text-slate-400 font-mono">Compliance Application Stage</span>
                         <span className="text-[10px] text-amber-500 font-mono tracking-widest">DAY 12 - FINAL REVIEW</span>
                       </div>
                    </div>
                 </div>

                 {/* Target 2: Communications Associate */}
                 <div className="bg-black/40 border border-slate-700/50 rounded flex flex-col hover:border-indigo-500/50 transition-colors">
                    <div className="p-4 border-b border-slate-800 flex justify-between items-center">
                       <div>
                         <span className="text-lg text-white font-bold font-sans uppercase">Communications Associate</span>
                         <span className="text-[10px] text-slate-500 font-mono uppercase tracking-widest block">COMMUNICATIONS NODE</span>
                       </div>
                       <span className="text-blue-400 bg-blue-900/20 px-2 py-1 rounded border border-blue-500/30 text-[10px] font-mono tracking-widest uppercase font-bold">INITIATING</span>
                    </div>
                    <div className="p-4 space-y-3">
                       <div className="flex justify-between items-center">
                         <span className="text-xs text-slate-400 font-mono">Proof of Role Necessity</span>
                         <div className="w-4 h-4 rounded-full border-2 border-amber-500 flex items-center justify-center">
                           <div className="w-1 h-1 bg-amber-500 rounded-full animate-pulse"></div>
                         </div>
                       </div>
                       <div className="flex justify-between items-center">
                         <span className="text-xs text-slate-400 font-mono">Invitation to Join (HO)</span>
                         <span className="text-[10px] text-slate-500 font-mono">PENDING DRAFT</span>
                       </div>
                       <div className="flex justify-between items-center">
                         <span className="text-xs text-slate-400 font-mono">Compliance Application Stage</span>
                         <span className="text-[10px] text-slate-500 font-mono tracking-widest">PRE-SUBMISSION</span>
                       </div>
                    </div>
                 </div>
              </div>

              <div className="bg-slate-900/40 border border-indigo-500/20 rounded p-4 text-xs font-mono text-slate-400">
                <span className="text-indigo-400 font-bold tracking-widest uppercase mb-1 block">The Gavel Strike Protocol</span>
                If a compliance is delayed &gt; 15 days, generate an "Institutional Mandate of Inquiry" citing company growth impact.
              </div>
            </div>
          )}

          {activeTab === 'TASK_QUEUE' && (
            <div className="p-8 flex flex-col space-y-6 overflow-y-auto custom-scrollbar">
              <div className="flex justify-between items-center border-b border-indigo-500/20 pb-4">
                <div>
                  <h3 className="text-xl font-bold text-white uppercase tracking-wide font-sans">Corporate Inbox & Scribing</h3>
                  <span className="text-slate-400 font-mono text-[10px] uppercase tracking-widest block mt-1">Watching live Client Resource Workflow threads</span>
                </div>
                {autoScribeEnabled && (
                  <span className="flex items-center gap-1.5 font-mono text-[10px] bg-emerald-950/40 border border-emerald-500/30 text-emerald-400 px-2.5 py-1 rounded animate-pulse">
                     <span className="w-2 h-2 rounded-full bg-emerald-400"></span> WATCHING FEED
                  </span>
                )}
              </div>

              {/* TWO COLUMN INBOX / AUTOSCRIBE RESULTS */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-1">
                {/* Incoming Messages list */}
                <div className="space-y-3">
                  <span className="text-slate-500 font-mono text-[10px] uppercase tracking-widest block">Incoming Email Feed</span>
                  <div className="space-y-2">
                    {MOCK_EMAILS.map((email) => (
                      <div 
                        key={email.id} 
                        onClick={() => {
                          setSelectedMail(email);
                          setScribeResult(null);
                        }}
                        className={`p-3.5 rounded border transition-all cursor-pointer text-left ${
                          selectedMail?.id === email.id 
                            ? 'bg-indigo-950/30 border-indigo-500/50 shadow-[0_0_10px_rgba(99,102,241,0.2)]' 
                            : 'bg-black/30 border-slate-800 hover:border-slate-700'
                        }`}
                      >
                        <div className="flex justify-between text-slate-500 font-mono text-[9px] uppercase tracking-wider mb-1">
                          <span>{email.sender.slice(0, 24)}...</span>
                          <span>{email.date}</span>
                        </div>
                        <h4 className="text-slate-200 text-xs font-bold leading-tight font-sans mb-1 truncate">{email.subject}</h4>
                        <p className="text-[10px] text-slate-400 font-sans line-clamp-2">{email.body}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Scribing Work Area */}
                <div className="bg-black/40 border border-slate-800 rounded-xl p-4 flex flex-col justify-between min-h-[300px]">
                  {selectedMail ? (
                    <div className="space-y-4 flex-1 flex flex-col justify-between">
                      <div>
                        <div className="border-b border-slate-800 pb-2 mb-3">
                          <span className="text-[10px] font-mono text-purple-400 uppercase tracking-widest block mb-1">Active Email Core</span>
                          <h4 className="text-white text-xs font-bold font-sans">{selectedMail.subject}</h4>
                          <span className="text-[9px] text-slate-500 font-mono">From: {selectedMail.sender}</span>
                        </div>
                        
                        <p className="text-[10px] font-mono text-slate-400 bg-black/60 border border-slate-900 rounded p-3 italic mb-4 leading-relaxed">
                          "{selectedMail.body}"
                        </p>
                      </div>

                      {/* Gemini scribe triggers */}
                      <div className="space-y-3">
                        {isScribing ? (
                          <div className="flex items-center justify-center gap-2 p-6 font-mono text-xs text-indigo-400 animate-pulse bg-indigo-950/20 rounded border border-indigo-500/30">
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            <span>GEMINI AI_ENGINE EXTRACTING LEAD SCHEMA...</span>
                          </div>
                        ) : scribeResult ? (
                          <div className="space-y-3 animate-fadeIn">
                            <div className={`p-3 rounded border font-mono text-[9px] ${
                              scribeResult.isClientLead 
                                ? 'bg-emerald-950/30 border-emerald-500/30 text-emerald-400' 
                                : 'bg-amber-950/30 border-amber-500/30 text-amber-500'
                            }`}>
                              {scribeResult.isClientLead ? (
                                <>
                                  <div className="font-bold uppercase tracking-widest mb-1 flex items-center gap-1.5">
                                    <CheckCircle className="w-3.5 h-3.5" /> Client Resource Workflow LEAD RECOGNIZED
                                  </div>
                                  <div>Detected Candidate: <span className="text-white font-bold">{scribeResult.candidateName}</span></div>
                                </>
                              ) : (
                                <div className="font-bold uppercase tracking-widest mb-1">
                                  ● NON-Client Resource Workflow INQUIRY / DETECTED QUARANTINE
                                </div>
                              )}
                            </div>

                            {scribeResult.isClientLead && scribeResult.draft && (
                              <div className="space-y-2">
                                <span className="text-[9px] font-mono uppercase tracking-widest text-slate-400 block">Scribed Secure Invoice Register Invitation Draft:</span>
                                <div className="p-3 bg-black/80 border border-indigo-500/20 rounded text-[9px] font-mono text-indigo-200 leading-relaxed whitespace-pre-wrap max-h-40 overflow-y-auto">
                                  <strong>Subject:</strong> {scribeResult.subject}<br/><br/>
                                  {scribeResult.draft}
                                </div>
                                <button 
                                  onClick={() => {
                                    setLog(prev => [{
                                      timestamp: new Date().toLocaleTimeString(),
                                      msg: `Secure Invoice Register DISPATCH: Scribed invitation for ${scribeResult.candidateName} sent to email outbox.`
                                    }, ...prev]);
                                    setScribeResult(null);
                                  }}
                                  className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-mono text-[9px] font-bold tracking-widest uppercase p-2.5 rounded transition-all shadow-[0_0_10px_rgba(99,102,241,0.3)] flex items-center justify-center gap-2"
                                >
                                  <Send className="w-4 h-4" /> DISPATCH APPROVED INVITATION
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={() => executeAutoScribe(selectedMail)}
                            disabled={!autoScribeEnabled}
                            className="w-full bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-950/50 border border-slate-700/50 rounded p-3 text-center transition-all flex items-center justify-center gap-2 group"
                          >
                            <Bot className="w-4 h-4 text-indigo-400 group-hover:animate-bounce" />
                            <span className="font-mono text-[10px] uppercase font-bold text-white tracking-widest">
                              {autoScribeEnabled ? "Scribe with Gemini AI_ENGINE" : "Auto-Scribe Locked"}
                            </span>
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-slate-600 italic font-mono text-center text-[10px] p-6">
                      <Mail className="w-8 h-8 opacity-20 mb-2" />
                      Awaiting email selection...
                    </div>
                  )}
                </div>
              </div>

              {/* REAL-TIME QUEUED OPERATIONS & LEDGER MATRIX */}
              <div className="border-t border-indigo-500/20 pt-6 mt-6 space-y-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h4 className="text-sm font-black text-white font-sans uppercase tracking-widest flex items-center gap-2">
                      <Cpu className="w-4 h-4 text-[#00e5ff] animate-spin" /> REAL-TIME QUEUED OPERATIONS SHIELD (SYS-07)
                    </h4>
                    <span className="text-slate-400 font-mono text-[9px] uppercase tracking-wider block mt-1">
                      INTEGRATED SYSTEM METRICS FROM LEDGER CORE NODE // ACTIVE ROUTE: SECURE PAYMENT PROVIDER
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={handleEnqueueNewOperation}
                      className="px-3 py-1.5 bg-indigo-950/40 hover:bg-indigo-900 border border-indigo-500/30 text-indigo-400 rounded text-[9px] font-mono font-bold tracking-widest uppercase transition-colors flex items-center gap-1.5 cursor-pointer"
                    >
                      <PlusCircle className="w-3.5 h-3.5" /> Enqueue task
                    </button>
                    <button
                      type="button"
                      onClick={handleForceSettleSync}
                      className="px-3 py-1.5 bg-cyan-950/40 hover:bg-cyan-900 border border-[#00e5ff]/30 text-[#00e5ff] rounded text-[9px] font-mono font-bold tracking-widest uppercase transition-colors flex items-center gap-1.5 cursor-pointer shadow-[0_0_10px_rgba(0,229,255,0.1)]"
                    >
                      <Zap className="w-3.5 h-3.5 text-[#00e5ff] animate-pulse" /> Force Full Sync
                    </button>
                    <button
                      type="button"
                      onClick={refreshLedger}
                      disabled={ledgerLoading}
                      className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300 rounded text-[9px] font-mono font-bold tracking-widest uppercase transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                    >
                      <RefreshCw className={`w-3 h-3 ${ledgerLoading ? 'animate-spin' : ''}`} /> Refresh
                    </button>
                  </div>
                </div>

                {/* LEDGER FEEDBACK STRIP */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 font-mono text-[10px]">
                  <div className="bg-[#030712]/60 border border-slate-800 p-2.5 rounded flex flex-col justify-between">
                    <span className="text-slate-500 text-[8px] uppercase">Ledger Source Code</span>
                    <span className="text-white font-bold mt-1">SC887095</span>
                  </div>
                  <div className="bg-[#030712]/60 border border-slate-800 p-2.5 rounded flex flex-col justify-between">
                    <span className="text-slate-500 text-[8px] uppercase">Registered Gross</span>
                    <span className="text-[#00e5ff] font-bold mt-1">£{(metrics?.totalGrossHarvest || 2820000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="bg-[#030712]/60 border border-slate-800 p-2.5 rounded flex flex-col justify-between">
                    <span className="text-slate-500 text-[8px] uppercase">3% Community Allocation</span>
                    <span className="text-amber-500 font-bold mt-1">£{(metrics?.NexoraXLevy || 28200).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="bg-[#030712]/60 border border-slate-800 p-2.5 rounded flex flex-col justify-between">
                    <span className="text-slate-500 text-[8px] uppercase">Vault Connectivity</span>
                    <span className="text-emerald-400 font-bold mt-1 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
                      {metrics?.isLive ? "LIVE SYNCED" : "BUFFER LOCAL"}
                    </span>
                  </div>
                </div>

                {/* MINI PERFORMANCE INSIGHTS DASHBOARD */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 text-left mb-2">
                  {/* Left Column: Line Chart */}
                  <div className="lg:col-span-8 bg-[#030712]/75 border border-slate-800/80 p-4 rounded-xl flex flex-col space-y-3 shadow-2xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/[0.015] rounded-full blur-3xl pointer-events-none"></div>
                    <div className="flex justify-between items-center border-b border-slate-800 pb-2">
                      <div>
                        <h5 className="font-sans font-bold text-xs text-white uppercase tracking-wider flex items-center gap-1.5">
                          <TrendingUp className="w-3.5 h-3.5 text-[#00e5ff]" /> 30-Day Solved SLA Rate & Completion Velocity
                        </h5>
                        <span className="text-[9px] text-slate-500 font-mono uppercase">Direct tracking of corporate integration handshakes</span>
                      </div>
                      <div className="flex items-center gap-2 font-mono text-[9px]">
                        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 bg-[#00e5ff] rounded-full"></span> Completion Output %</span>
                      </div>
                    </div>

                    {/* LineChart Render container */}
                    <div className="h-44 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={taskCompletionRateData} margin={{ top: 5, right: 10, left: -25, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#0f172a" />
                          <XAxis 
                            dataKey="day" 
                            stroke="#475569" 
                            fontSize={8} 
                            tickLine={false} 
                            tickFormatter={(tick) => tick.replace('Day ', 'D')}
                            interval={3}
                          />
                          <YAxis 
                            stroke="#475569" 
                            fontSize={8} 
                            tickLine={false} 
                            domain={[60, 100]}
                            tickFormatter={(tick) => `${tick}%`}
                          />
                          <Tooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <div className="bg-[#030712] border border-indigo-500/30 p-2.5 rounded shadow-xl font-mono text-[9px] space-y-1 text-slate-300">
                                    <p className="text-white font-bold">{data.day}</p>
                                    <p>SLA Completion: <span className="text-[#00e5ff] font-bold">{payload[0].value}%</span></p>
                                    <p>Solved Volume: <span className="text-amber-500 font-bold">{data.solved} / {data.total} tasks</span></p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="rate" 
                            stroke="#00e5ff" 
                            strokeWidth={1.8} 
                            dot={{ fill: '#0a0f1d', stroke: '#00e5ff', strokeWidth: 1.5, r: 2.5 }} 
                            activeDot={{ r: 4.5, strokeWidth: 0, fill: '#00e5ff' }} 
                            name="SLA Rate"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Right Column: Key Performance Insights */}
                  <div className="lg:col-span-4 bg-[#030712]/75 border border-slate-800 p-4 rounded-xl flex flex-col justify-between space-y-3 relative overflow-hidden font-mono text-xs">
                    <div className="absolute top-0 left-0 w-32 h-32 bg-cyan-500/[0.015] rounded-full blur-2xl pointer-events-none"></div>
                    
                    <div className="border-b border-slate-800 pb-2">
                      <h5 className="font-sans font-bold text-xs text-white uppercase tracking-wider flex items-center gap-1.5">
                        <Activity className="w-3.5 h-3.5 text-indigo-400" /> Director Insights
                      </h5>
                      <span className="text-[9px] text-slate-500 uppercase">Live calibrated metrics</span>
                    </div>

                    <div className="space-y-2.5 flex-1 flex flex-col justify-center">
                      <div className="flex justify-between items-center text-[10px] border-b border-slate-900 pb-1.5">
                        <span className="text-slate-500 uppercase">SLA Target Fulfillment</span>
                        <span className="text-emerald-400 font-black flex items-center gap-1">
                          98.45% <span className="text-[8px] bg-emerald-950/50 px-1 rounded text-emerald-400 border border-emerald-500/20">+1.2%</span>
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-[10px] border-b border-slate-900 pb-1.5">
                        <span className="text-slate-500 uppercase">Avg Handshake Latency</span>
                        <span className="text-indigo-400 font-black">0.8s standard</span>
                      </div>
                      <div className="flex justify-between items-center text-[10px] border-b border-slate-900 pb-1.5">
                        <span className="text-slate-500 uppercase">30-Day Peak Output</span>
                        <span className="text-amber-500 font-black">54 tasks settled/day</span>
                      </div>
                      <div className="flex justify-between items-center text-[10px] pb-1">
                        <span className="text-slate-500 uppercase">Secure Mirror Replication</span>
                        <span className="text-[#00e5ff] font-black uppercase text-[10px]">Live Linked</span>
                      </div>
                    </div>

                    <div className="bg-indigo-950/15 border border-indigo-500/20 p-2 rounded text-[9px] text-slate-400 leading-normal">
                      <span className="text-indigo-300 font-bold uppercase block mb-0.5">Automated Analysis</span>
                      Operational flow rate is optimal. Auto-billing placements are executing with zero manual intervention needed.
                    </div>
                  </div>
                </div>

                {/* AI-POWERED SCRIBE TASK INPUT BAR */}
                <div className="bg-[#030612]/95 border border-indigo-500/20 p-4 rounded-xl flex flex-col space-y-3 shadow-xl text-left">
                  <div className="flex justify-between items-center border-b border-slate-800/60 pb-2">
                    <span className="text-[10px] uppercase font-mono tracking-widest text-[#00e5ff] font-bold flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-[#00e5ff] animate-pulse" /> AI Task Scribe Engine (Node-18.5)
                    </span>
                    <span className="text-slate-500 font-mono text-[9px] uppercase">
                      Press [Enter] while inside to queue, or type & submit
                    </span>
                  </div>

                  <div className="flex gap-2.5 items-center">
                    <input
                      id="ai-task-scribe-input"
                      type="text"
                      placeholder="Scribe new task: e.g., 'Verification process for Stripe compliance and fee ledger re-indexing'..."
                      value={newTaskName}
                      onChange={(e) => setNewTaskName(e.target.value)}
                      className="flex-1 bg-black/55 border border-slate-800/80 hover:border-slate-700/85 focus:border-indigo-500 rounded-lg px-3.5 py-2.5 text-xs text-white placeholder-slate-500 font-mono focus:outline-none focus:ring-1 focus:ring-indigo-500/20 transition-all font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => handleAddScribedTask(newTaskName)}
                      disabled={!newTaskName.trim()}
                      className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed border border-indigo-500/40 text-white rounded-lg text-xs font-mono font-bold tracking-widest uppercase transition-all shadow-[0_0_10px_rgba(99,102,241,0.25)] flex items-center gap-1.5 cursor-pointer"
                    >
                      <PlusCircle className="w-4 h-4" /> Queue Scribe
                    </button>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 sm:items-center font-mono text-[10px] text-slate-400 bg-slate-950/45 p-2 rounded border border-slate-900">
                    <div className="flex items-center gap-1.5">
                      <span>Set Due Date (Optional):</span>
                      <input
                        type="datetime-local"
                        value={newTaskDueDate}
                        onChange={(e) => setNewTaskDueDate(e.target.value)}
                        className="bg-black/60 border border-slate-800 rounded px-2 py-1 text-slate-200 focus:outline-none focus:border-indigo-500 cursor-pointer font-mono text-[10px] w-48"
                      />
                    </div>
                    <span className="text-slate-600 hidden sm:inline">|</span>
                    <span>High-priority scribes will automatically fallback to a default 50-minute deadline if left blank.</span>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 text-[9px] font-mono text-slate-500 uppercase items-center">
                    <span className="font-bold text-slate-400">Suggested AI triggers:</span>
                    <button type="button" onClick={() => setNewTaskName("Audit Stripe billing fee ledgers")} className="hover:text-[#00e5ff] transition-colors border border-slate-900 bg-slate-950/40 px-1.5 py-0.5 rounded cursor-pointer">"Audit Stripe billing..."</button>
                    <button type="button" onClick={() => setNewTaskName("Verify Home Office sponsorship license contracts")} className="hover:text-[#00e5ff] transition-colors border border-slate-900 bg-slate-950/40 px-1.5 py-0.5 rounded cursor-pointer">"Verify Home Office..."</button>
                    <button type="button" onClick={() => setNewTaskName("Optimize neural core training pipeline")} className="hover:text-[#00e5ff] transition-colors border border-slate-900 bg-slate-950/40 px-1.5 py-0.5 rounded cursor-pointer">"Optimize neural core..."</button>
                  </div>
                </div>

                {/* SEARCH, SORT, FILTERS CONTROL HUB */}
                <div className="bg-slate-950/40 border border-slate-800 p-4 rounded-xl flex flex-col md:flex-row gap-3 items-center justify-between font-mono text-xs">
                  {/* Search Input with Icon */}
                  <div className="relative w-full md:w-72">
                    <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
                    <input 
                      type="text"
                      placeholder="Search queue, categories..."
                      value={queueSearch}
                      onChange={(e) => setQueueSearch(e.target.value)}
                      className="w-full bg-[#030712]/90 border border-slate-800 rounded-lg pl-9 pr-3 py-2 text-white text-xs focus:outline-none focus:border-indigo-500"
                    />
                  </div>

                  {/* Actions / Filters */}
                  <div className="flex flex-wrap gap-2.5 w-full md:w-auto items-center justify-end">
                    {/* Export to CSV Option */}
                    <button
                      type="button"
                      onClick={handleExportToCsv}
                      className="px-3 py-1.5 bg-emerald-950/50 hover:bg-emerald-900 border border-emerald-500/30 text-emerald-400 rounded-lg text-xs font-mono font-bold uppercase transition-all flex items-center gap-1.5 cursor-pointer"
                      title="Export active tasks queue to CSV file"
                    >
                      <Download className="w-4 h-4" /> Export CSV
                    </button>

                    {/* Due Date Alarm Reminder Toggle */}
                    <button
                      type="button"
                      onClick={handleToggleReminder}
                      className={`px-3 py-1.5 border rounded-lg text-xs font-mono font-bold uppercase transition-all flex items-center gap-1.5 cursor-pointer ${
                        dueDateReminderEnabled 
                          ? 'bg-purple-950/50 border-purple-500/30 text-purple-400 hover:bg-purple-900/60' 
                          : 'bg-[#030712]/90 border-slate-800 text-slate-500 hover:bg-slate-800'
                      }`}
                      title="Toggle browser notification due date alerts"
                    >
                      <AlertCircle className="w-4 h-4" /> Reminders: {dueDateReminderEnabled ? "ON" : "OFF"}
                    </button>

                    {/* Priority Filter */}
                    <div className="flex items-center gap-1.5 bg-[#030712]/80 border border-slate-800 rounded-lg px-2.5 py-1.5 text-slate-400">
                      <span>Priority:</span>
                      <select 
                        value={queuePriorityFilter}
                        onChange={(e) => setQueuePriorityFilter(e.target.value as any)}
                        className="bg-transparent text-slate-200 font-bold font-sans border-none focus:outline-none cursor-pointer text-xs"
                      >
                        <option value="ALL">ALL</option>
                        <option value="HIGH">HIGH</option>
                        <option value="MEDIUM">MEDIUM</option>
                        <option value="LOW">LOW</option>
                      </select>
                    </div>

                    {/* Status Filter */}
                    <div className="flex items-center gap-1.5 bg-[#030712]/80 border border-slate-800 rounded-lg px-2.5 py-1.5 text-slate-400">
                      <span>Status:</span>
                      <select 
                        value={queueStatusFilter}
                        onChange={(e) => setQueueStatusFilter(e.target.value as any)}
                        className="bg-transparent text-slate-200 font-bold font-sans border-none focus:outline-none cursor-pointer text-xs"
                      >
                        <option value="ALL">ALL</option>
                        <option value="PROCESSING">PROCESSING</option>
                        <option value="PENDING">PENDING</option>
                        <option value="COMPLETED">COMPLETED</option>
                      </select>
                    </div>

                    {/* Sort By Field */}
                    <div className="flex items-center gap-1.5 bg-[#030712]/80 border border-slate-800 rounded-lg px-2.5 py-1.5 text-slate-400">
                      <span>Sort:</span>
                      <select 
                        value={queueSortBy}
                        onChange={(e) => setQueueSortBy(e.target.value as any)}
                        className="bg-transparent text-slate-200 font-bold font-sans border-none focus:outline-none cursor-pointer text-xs"
                      >
                        <option value="lastUpdated">Last Synced</option>
                        <option value="name">Alpha Name</option>
                        <option value="priority">Priority SLA</option>
                        <option value="progress">Progress %</option>
                      </select>
                      <button 
                        type="button"
                        title="Toggle sorting direction"
                        onClick={() => setQueueSortDir(prev => prev === 'asc' ? 'desc' : 'asc')}
                        className="text-indigo-400 hover:text-indigo-300 font-extrabold focus:outline-none transition-transform cursor-pointer"
                      >
                        <ArrowUpDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* BULK ACTIONS STRIP */}
                {selectedTaskIds.length > 0 && (
                  <div className="bg-indigo-950/20 border border-indigo-500/35 p-4 rounded-xl flex flex-col space-y-3 text-left">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <CheckSquare className="w-5 h-5 text-indigo-400 font-black animate-pulse" />
                        <span className="font-mono text-xs text-indigo-200">
                          Active Queue Selection: <strong className="text-white bg-indigo-500/35 px-2 py-0.5 rounded text-[11px] font-sans">{selectedTaskIds.length}</strong> operations queued
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-2 items-center font-mono text-[10px]">
                        <span className="text-slate-500 uppercase">Bulk Recalibration:</span>
                        <button
                          type="button"
                          onClick={() => handleBulkSetPriority('HIGH')}
                          className="px-2.5 py-1 bg-rose-950/60 border border-rose-500/30 text-rose-400 font-bold rounded hover:bg-rose-900/30 transition-all cursor-pointer"
                        >
                          SLA High Priority
                        </button>
                        <button
                          type="button"
                          onClick={() => handleBulkSetPriority('MEDIUM')}
                          className="px-2.5 py-1 bg-amber-950/60 border border-amber-500/30 text-amber-500 font-bold rounded hover:bg-amber-900/30 transition-all cursor-pointer"
                        >
                          SLA Med Priority
                        </button>
                        <button
                          type="button"
                          onClick={() => handleBulkSetPriority('LOW')}
                          className="px-2.5 py-1 bg-sky-950/60 border border-sky-500/30 text-sky-400 font-bold rounded hover:bg-[#0c4a6e]/30 transition-all cursor-pointer"
                        >
                          SLA Low Priority
                        </button>

                        <span className="text-slate-600">|</span>
                        <span className="text-slate-500 uppercase">Set Status:</span>
                        <button
                          type="button"
                          onClick={() => handleBulkSetStatus('PROCESSING')}
                          className="px-2.5 py-1 bg-cyan-950/60 border border-cyan-500/30 text-[#00e5ff] font-bold rounded hover:bg-cyan-900/30 transition-all cursor-pointer"
                        >
                          In Progress
                        </button>
                        <button
                          type="button"
                          onClick={() => handleBulkSetStatus('PENDING')}
                          className="px-2.5 py-1 bg-yellow-950/60 border border-yellow-500/30 text-yellow-500 font-bold rounded hover:bg-yellow-900/30 transition-all cursor-pointer"
                        >
                          Blocked
                        </button>
                        <button
                          type="button"
                          onClick={() => handleBulkSetStatus('COMPLETED')}
                          className="px-2.5 py-1 bg-emerald-950/60 border border-emerald-500/30 text-emerald-400 font-bold rounded hover:bg-emerald-900/30 transition-all cursor-pointer"
                        >
                          Completed
                        </button>

                        <span className="text-slate-600">|</span>
                        <button
                          type="button"
                          onClick={handleBulkDelete}
                          className="px-2.5 py-1 bg-red-950/60 border border-red-500/40 text-red-400 font-bold rounded hover:bg-red-900/40 transition-all flex items-center gap-1 cursor-pointer font-sans"
                        >
                          <Trash2 className="w-3 h-3" /> Bulk Delete
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedTaskIds([])}
                          className="px-2.5 py-1 bg-slate-900 border border-slate-700 text-slate-300 rounded hover:bg-slate-800 transition-all cursor-pointer"
                        >
                          Deselect All
                        </button>
                      </div>
                    </div>

                    {/* Batch Tagging section embedded beautifully */}
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between border-t border-indigo-500/10 pt-3 gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-slate-400 font-mono text-[10px] uppercase tracking-wider font-extrabold text-indigo-400">Batch Tagging:</span>
                        <input
                          type="text"
                          placeholder="Enter project/dept tag..."
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          className="bg-black/60 border border-indigo-500/25 text-white font-mono text-[11px] px-2.5 py-1 rounded w-44 focus:outline-none focus:border-indigo-500 placeholder-slate-600 focus:ring-1 focus:ring-indigo-500/30"
                        />
                        <button
                          type="button"
                          onClick={() => handleBulkApplyTag(tagInput)}
                          disabled={!tagInput.trim()}
                          className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-mono text-[10px] font-black uppercase px-3 py-1.5 rounded transition-all cursor-pointer"
                        >
                          Apply Tag
                        </button>
                      </div>

                      <div className="flex flex-wrap gap-1 items-center font-mono">
                        <span className="text-slate-500 text-[9px] uppercase font-bold mr-1">Quick Presets:</span>
                        {["Ledger", "Compliance", "AI Node", "Urgent", "Audit", "Operations"].map(presetTag => (
                          <button
                            key={presetTag}
                            type="button"
                            onClick={() => handleBulkApplyTag(presetTag)}
                            className="bg-slate-900 hover:bg-indigo-950/40 border border-slate-800 text-slate-400 text-[9px] px-2 py-0.5 rounded transition-all cursor-pointer"
                          >
                            +{presetTag}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* SELECT ALL VISIBLE TRACK ROW */}
                <div className="flex items-center justify-between px-3.5 py-2.5 bg-[#030712]/50 rounded-lg border border-slate-800/80 font-mono text-[9px] text-slate-500 uppercase tracking-widest mt-1">
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox"
                      checked={processedQueuedOps.length > 0 && processedQueuedOps.every(op => selectedTaskIds.includes(op.id))}
                      onChange={() => {
                        const visibleIds = processedQueuedOps.map(op => op.id);
                        const allSelected = visibleIds.every(id => selectedTaskIds.includes(id));
                        if (allSelected) {
                          setSelectedTaskIds(prev => prev.filter(id => !visibleIds.includes(id)));
                        } else {
                          setSelectedTaskIds(prev => Array.from(new Set([...prev, ...visibleIds])));
                        }
                      }}
                      className="w-3.5 h-3.5 rounded border-slate-800 bg-[#030712] accent-indigo-500 checked:bg-indigo-600 focus:ring-0 cursor-pointer"
                    />
                    <span>Select All Filtered ({processedQueuedOps.length} Visible Tasks)</span>
                  </div>
                  <div>
                    <span>Status: OPERATIONAL INGESTION PIPELINE // OK</span>
                  </div>
                </div>

                {/* PROGRESS MARKS TABLE / CARDS */}
                <div className="space-y-2 max-h-[400px] overflow-y-auto custom-scrollbar">
                  <AnimatePresence initial={false}>
                    {processedQueuedOps.map((op) => {
                      const isSelected = selectedTaskIds.includes(op.id);
                      return (
                        <motion.div 
                          layout
                          key={op.id}
                          initial={{ opacity: 0, height: 0, y: -10 }}
                          animate={{ opacity: 1, height: 'auto', y: 0 }}
                          exit={{ opacity: 0, height: 0, y: 10 }}
                          transition={{ duration: 0.22 }}
                          className={`border p-3 rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-colors relative overflow-hidden ${
                            isSelected 
                              ? 'bg-indigo-950/35 border-indigo-500/40 shadow-[0_0_10px_rgba(99,102,241,0.15)]' 
                              : 'bg-slate-950/55 hover:bg-slate-900/60 border border-slate-800/80'
                          }`}
                        >
                          {/* background track for progress glow */}
                          <div 
                            className="absolute left-0 top-0 bottom-0 bg-[#00e5ff]/[0.025] transition-all duration-500 pointer-events-none" 
                            style={{ width: `${op.progress}%` }}
                          />

                          <div className="flex items-center gap-3 relative z-10 flex-1 min-w-0">
                            {/* Multi-select Checkbox */}
                            <input 
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleTaskSelect(op.id)}
                              className="w-4 h-4 rounded border-slate-700 bg-slate-900 accent-indigo-500 checked:bg-indigo-600 focus:ring-0 cursor-pointer shrink-0"
                            />

                            <div className="flex-1 min-w-0 space-y-1 text-left">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-sans font-bold text-xs text-white uppercase tracking-wide truncate">
                                  {op.name}
                                </span>
                                <span className="font-mono text-[8px] bg-slate-900 border border-slate-800 px-1.5 py-0.5 rounded text-slate-400 uppercase">
                                  {op.node}
                                </span>
                                <span className={`font-mono text-[8px] font-extrabold px-1.5 py-0.5 rounded border uppercase tracking-widest ${
                                  op.priority === 'HIGH' ? 'bg-rose-500/15 border-rose-500/45 text-rose-400' :
                                  op.priority === 'MEDIUM' ? 'bg-amber-500/15 border-amber-500/40 text-amber-400' :
                                  'bg-sky-500/10 border-sky-500/30 text-sky-400'
                                }`}>
                                  {op.priority || 'MEDIUM'}
                                </span>
                                {op.tags && op.tags.map((tag: string) => (
                                  <span key={tag} className="font-mono text-[8px] font-extrabold px-1.5 py-0.5 rounded border bg-[#00e5ff]/5 border-[#00e5ff]/30 text-[#00e5ff] uppercase tracking-widest flex items-center gap-0.5 shadow-[0_0_5px_rgba(0,229,255,0.05)]">
                                    <Sparkles className="w-2.5 h-2.5 text-[#00e5ff]" /> {tag}
                                  </span>
                                ))}
                                {op.dueDate && (
                                  <span className={`font-mono text-[8px] font-black px-1.5 py-0.5 rounded border uppercase tracking-widest flex items-center gap-1 ${
                                    (new Date(op.dueDate).getTime() - Date.now()) <= 60 * 60 * 1000 && (new Date(op.dueDate).getTime() - Date.now()) > 0
                                      ? 'bg-rose-500/20 border-rose-500/50 text-rose-300 animate-pulse'
                                      : 'bg-purple-500/10 border-purple-500/30 text-purple-400'
                                  }`}>
                                    <Clock className="w-2.5 h-2.5" /> Due: {new Date(op.dueDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ({Math.round((new Date(op.dueDate).getTime() - Date.now()) / 60000)}m left)
                                  </span>
                                )}
                                <span className="font-mono text-[8px] text-slate-500">
                                  {op.category}
                                </span>
                              </div>
                              <div className="font-mono text-[9px] text-slate-400 flex items-center gap-1">
                                <Clock className="w-3 h-3 text-slate-500" />
                                <span>Status:</span>
                                <span className={`${
                                  op.status === 'COMPLETED' ? 'text-emerald-400 font-bold' : 
                                  op.status === 'PROCESSING' ? 'text-[#00e5ff] font-bold animate-pulse' : 
                                  'text-amber-500'
                                }`}>
                                  {op.stepName}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Right feedback block / Progress markers */}
                          <div className="flex items-center gap-4 shrink-0 relative z-10 justify-between sm:justify-end">
                            {op.payloadValue > 0 && (
                              <div className="text-right font-mono">
                                <div className="text-[7px] text-slate-500 uppercase">Payload Weight</div>
                                <div className="text-[10px] font-bold text-slate-300">£{op.payloadValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                              </div>
                            )}

                            <div className="flex flex-col items-end gap-1 font-mono">
                              <div className="flex items-center gap-2">
                                <span className="text-[8px] text-slate-500 uppercase">{op.lastUpdated}</span>
                                <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded border ${
                                  op.status === 'COMPLETED' ? 'bg-emerald-950/40 border-emerald-500/30 text-emerald-400' :
                                  op.status === 'PROCESSING' ? 'bg-cyan-950/40 border-[#00e5ff]/30 text-[#00e5ff] animate-pulse' :
                                  'bg-amber-950/40 border-amber-500/30 text-amber-500'
                                }`}>
                                  {op.status}
                                </span>
                              </div>
                              
                              {/* Progress gauge bar */}
                              <div className="w-28 bg-slate-900 h-1.5 rounded-full overflow-hidden border border-slate-800">
                                <div 
                                  className={`h-full transition-all duration-500 ${
                                    op.status === 'COMPLETED' ? 'bg-emerald-500' :
                                    op.status === 'PROCESSING' ? 'bg-[#00e5ff]' :
                                    'bg-amber-500'
                                  }`}
                                  style={{ width: `${op.progress}%` }}
                                />
                              </div>
                              <span className="text-[9px] text-slate-400 font-bold self-end">{op.progress}%</span>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}

                    {processedQueuedOps.length === 0 && (
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="text-center font-mono text-xs text-slate-500 p-8 border border-dashed border-slate-800 rounded-lg bg-black/20"
                      >
                        <Bot className="w-8 h-8 text-indigo-400/30 mx-auto mb-2 animate-bounce" />
                        No operations matching the selected filter criteria.
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'Secure Invoice Register_DISPATCH' && (
            <div className="p-8 flex flex-col space-y-6 overflow-y-auto custom-scrollbar">
              <div className="flex justify-between items-center border-b border-indigo-500/30 pb-4">
                 <div>
                   <h3 className="text-xl font-bold text-white uppercase tracking-wide font-sans">Secure Invoice Register DISPATCH CONTROL</h3>
                   <span className="text-slate-400 text-xs font-mono tracking-widest mt-1 block">ONE-CLICK EMAIL DEPLOYMENT</span>
                 </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {['Claire', 'Helen', 'Eleanor'].map((name) => (
                  <div key={name} className="bg-black/40 border border-slate-700/50 p-4 rounded flex flex-col items-center justify-center space-y-4 relative group hover:border-indigo-500 transition-colors">
                     <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center group-hover:bg-indigo-900/50 transition-colors">
                        <UserCheck className="w-6 h-6 text-slate-400 group-hover:text-indigo-400" />
                     </div>
                     <span className="font-sans font-bold text-lg text-white">{name}</span>
                     <button 
                       onClick={() => {
                         setLog(prev => [{
                           timestamp: new Date().toLocaleTimeString(),
                           msg: `Secure Invoice Register dispatch: Manual mandate sent to candidate ${name} direct queue.`
                         }, ...prev]);
                       }}
                       className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-mono text-[10px] font-bold tracking-widest uppercase px-4 py-2 rounded transition-colors w-full justify-center"
                     >
                       <Send className="w-4 h-4" /> DISPATCH
                     </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'ARCHIVES' && (
            <div className="p-8 flex flex-col space-y-6 overflow-y-auto h-full custom-scrollbar">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-slate-800 pb-4 gap-4">
                <div>
                  <h3 className="text-xl font-bold text-white uppercase tracking-wide font-sans flex items-center gap-2">
                    <Archive className="w-5 h-5 text-indigo-400" /> Secretary Archive Vault
                  </h3>
                  <span className="text-slate-400 text-xs font-mono tracking-widest mt-1 block">NODE-18.5 LEDGER INTEGRITY ENGINE</span>
                </div>
                <button 
                  onClick={fetchLedger}
                  disabled={fetchingLedger}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-slate-900 border border-slate-800 text-slate-400 font-mono text-[9px] uppercase tracking-wider hover:text-white transition-colors"
                >
                  <RefreshCw className={`w-3 h-3 ${fetchingLedger ? 'animate-spin' : ''}`} /> Sync Live Ledger
                </button>
              </div>

              {/* TWO COLUMN INTERACTION PLATFORM */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                {/* Column A: Controls and Current Ledger State */}
                <div className="space-y-6">
                  <div className="bg-[#030612]/90 border border-indigo-500/10 rounded-xl p-5 space-y-4">
                    <span className="text-[10px] uppercase font-mono tracking-widest text-indigo-400 font-black block">LEDGER PROTOCOLS</span>
                    <h4 className="text-white font-sans text-sm font-semibold">Active Period: May 2026</h4>
                    
                    <div className="grid grid-cols-2 gap-4 text-left font-mono">
                      <div className="bg-black/50 p-3 rounded border border-slate-900">
                        <div className="text-[8px] text-slate-500 uppercase">Gross Harvest</div>
                        <div className="text-sm font-bold text-white mt-1">£{(currentMonthGross || 4270000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      </div>
                      <div className="bg-black/50 p-3 rounded border border-slate-900">
                        <div className="text-[8px] text-slate-500 uppercase">Net Yield</div>
                        <div className="text-sm font-bold text-emerald-400 mt-1">£{(currentMonthNet || 3381840).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      </div>
                      <div className="bg-black/50 p-3 rounded border border-slate-900">
                        <div className="text-[8px] text-slate-500 uppercase">3% Community Allocation (1%)</div>
                        <div className="text-sm font-bold text-amber-500 mt-1">£{(currentMonthLevy || 42700).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      </div>
                      <div className="bg-black/50 p-3 rounded border border-slate-900">
                        <div className="text-[8px] text-slate-500 uppercase">10% Maintenance & Hosting Fee (20%)</div>
                        <div className="text-sm font-bold text-rose-500 mt-1">£{(currentMonthTax || 845460).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      </div>
                    </div>

                    <button 
                      onClick={handleGeneratePdfArchive}
                      disabled={pdfGenerating}
                      className="w-full mt-2 py-3 bg-gradient-to-r from-indigo-600 to-blue-700 hover:from-indigo-500 hover:to-blue-600 border border-indigo-400/50 text-white font-bold font-mono text-[10px] tracking-widest uppercase rounded-lg transition-all shadow-lg shadow-indigo-600/25 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {pdfGenerating ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          <span>Executing PDF Compilation...</span>
                        </>
                      ) : (
                        <>
                          <FileText className="w-4 h-4" />
                          <span>Generate PDF Archive Summary</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Operational Notes */}
                  <div className="bg-[#050a1b]/40 border border-amber-500/10 rounded-xl p-5 space-y-3 font-sans text-xs text-slate-400">
                    <span className="font-mono text-[9px] uppercase tracking-widest font-bold text-amber-500 flex items-center gap-1.5">
                      <Shield className="w-3.5 h-3.5" /> DEPLOYMENT COMPLIANCE METRICS
                    </span>
                    <p className="leading-relaxed">
                      All compiled archive documents will automatically execute the required multi-tier NexoraX tax logic (1% Perpetual 3% Community Allocation split exactly 33.33% to Charities 1, 2, & 3, alongside the 20% Lifetime 10% Maintenance & Hosting Fee), automatically routing the clear Net Settlement to **Secure Payment Provider**.
                    </p>
                    <p className="leading-relaxed border-t border-slate-800/60 pt-2 text-[10px] text-slate-500 font-mono">
                      Target Bank Route: Sort Code Verified Payment Configuration | Account Number Secure Billing Provider
                    </p>
                  </div>
                </div>

                {/* Column B: PDF Document Preview Screen */}
                <div className="flex flex-col">
                  {pdfGeneratedData ? (
                    <div className="bg-slate-900 border border-indigo-500/30 rounded-xl overflow-hidden flex flex-col shadow-2xl animate-fadeIn">
                      {/* Document Toolbar */}
                      <div className="bg-slate-950 px-4 py-2 flex justify-between items-center border-b border-indigo-500/20">
                        <span className="text-[9px] font-mono text-indigo-400 font-bold uppercase tracking-widest flex items-center gap-1.5 animate-pulse">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block"></span> READY FOR TRANSMISSION
                        </span>
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => window.print()}
                            className="bg-indigo-950/40 hover:bg-indigo-900/40 border border-indigo-500/30 px-3 py-1 rounded text-white font-mono text-[8px] uppercase tracking-wider flex items-center gap-1 cursor-pointer transition-colors"
                          >
                            <Download className="w-3 h-3" /> Save Local PDF
                          </button>
                        </div>
                      </div>

                      {/* Actual Faux PDF Render Pane */}
                      <div className="bg-slate-50 p-6 sm:p-8 text-black text-left font-sans text-[11px] leading-relaxed relative overflow-hidden select-text border-t-8 border-indigo-800 shadow-inner">
                        {/* Elegant Watermark */}
                        <div className="absolute inset-0 select-none pointer-events-none opacity-[0.03] flex items-center justify-center rotate-45 text-black font-extrabold text-5xl tracking-widest uppercase">
                          NX_NEXORAX_LEDGER
                        </div>

                        {/* PDF content block */}
                        <div className="space-y-6">
                          <div className="text-[8px] font-mono text-slate-500 font-semibold tracking-wider text-center border-b border-slate-200 pb-2">
                            In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
                          </div>

                          <div className="flex justify-between items-start border-b border-slate-300 pb-4">
                            <div>
                              <h2 className="text-sm font-extrabold tracking-tight uppercase text-indigo-900">NEXORAX DIGITAL LTD</h2>
                              <div className="text-[9px] font-mono text-slate-500">Corporate Registry SC887095 | SC887095 Node-07</div>
                            </div>
                            <div className="text-right">
                              <span className="text-[8px] font-bold text-white bg-slate-900 px-1.5 py-0.5 rounded tracking-widest uppercase font-mono">NEXORAX ARCHIVE</span>
                              <div className="text-[9px] text-slate-500 font-mono mt-1">ID: {pdfGeneratedData.reportId}</div>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4 text-[9px] font-mono text-slate-600 bg-slate-100 p-3 rounded">
                            <div>
                              <strong>FROM:</strong> Lead Director, Director<br/>
                              <strong>DATE:</strong> {pdfGeneratedData.date}<br/>
                              <strong>FREQUENCY:</strong> Planet Telecom 08000 622827
                            </div>
                            <div className="text-right">
                              <strong>SYSTEM REPLICATION:</strong> CloudSync-02<br/>
                              <strong>LEDGER VAULT STATUS:</strong> {pdfGeneratedData.status}<br/>
                              <strong>MIRROR PROTOCOL:</strong> LivingBrain_Source_Core_V2
                            </div>
                          </div>

                          <div className="space-y-3">
                            <h3 className="text-xs font-black tracking-wider uppercase text-slate-800 border-b border-slate-200 pb-1">FINANCIAL METRICS: MAY 2026</h3>
                            
                            <table className="w-full text-left text-[9px]">
                              <thead>
                                <tr className="border-b border-slate-200 bg-slate-100 font-bold uppercase text-slate-700">
                                  <th className="p-2">ACCOUNTING HEAD</th>
                                  <th className="p-2 text-right">VALUE IN GBP (£)</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr className="border-b border-slate-100">
                                  <td className="p-2 font-medium">May 2026 Daily Gross Harvest</td>
                                  <td className="p-2 text-right font-mono font-bold">£{pdfGeneratedData.gross.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                </tr>
                                <tr className="border-b border-slate-100">
                                  <td className="p-2 font-medium">1% 3% Community Allocation (Charity Support)</td>
                                  <td className="p-2 text-right font-mono font-bold text-amber-700">£{pdfGeneratedData.levy.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                </tr>
                                <tr className="border-b border-slate-100 text-slate-500 italic">
                                  <td className="p-2 pl-4">└ Charity 1 (Dunfermline Foundation) split 33.33%</td>
                                  <td className="p-2 text-right font-mono">£{pdfGeneratedData.charitySplit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                </tr>
                                <tr className="border-b border-slate-100 text-slate-500 italic">
                                  <td className="p-2 pl-4">└ Charity 2 (Global Foundation) split 33.33%</td>
                                  <td className="p-2 text-right font-mono">£{pdfGeneratedData.charitySplit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                </tr>
                                <tr className="border-b border-slate-100 text-slate-500 italic">
                                  <td className="p-2 pl-4">└ Charity 3 (UK Foundation) split 33.33%</td>
                                  <td className="p-2 text-right font-mono">£{pdfGeneratedData.charitySplit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                </tr>
                                <tr className="border-b border-slate-100">
                                  <td className="p-2 font-medium">10% Maintenance & Hosting Fee (NexoraX 10% Maintenance & Hosting Fee)</td>
                                  <td className="p-2 text-right font-mono font-bold text-rose-700">£{pdfGeneratedData.tax.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                </tr>
                                <tr className="bg-slate-200 text-indigo-900 border-t border-indigo-300 font-extrabold uppercase">
                                  <td className="p-2.5">NET finance SETTLEMENT</td>
                                  <td className="p-2.5 text-right font-mono text-base">£{pdfGeneratedData.net.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          <div className="space-y-3 pt-2 text-[9px] font-mono text-slate-600">
                            <strong>FINANCIAL ROUTING PROTOCOL:</strong><br/>
                            This settlement is verified, locked, and cleared directly to **Secure Payment Provider** Master Node in London (Sort Code: Verified Payment Configuration | Account: Secure Billing Provider). This transfer is public-preview checks passed with UK financial regulations.
                            <br/><br/>
                            <strong>SECURE DEPOSIT BACKUP LOCATION:</strong><br/>
                            <span className="text-indigo-800 text-[8px] break-all select-all font-bold font-mono">{pdfGeneratedData.gdrivePath}</span>
                          </div>

                          <div className="border-t border-slate-200 pt-3 text-[8px] text-center text-slate-400 font-bold tracking-widest uppercase">
                            OFFICE OF THE DIRECTOR, Lead Director | STRICT CONFID_ONLY
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-slate-950/20 border-2 border-dashed border-slate-800 rounded-xl flex-1 flex flex-col items-center justify-center text-slate-500 italic font-mono text-center text-[10px] p-10 min-h-[350px]">
                      <FileText className="w-12 h-12 text-slate-600 mb-2 animate-pulse" />
                      Awaiting PDF Master compiling signal...<br/>
                      Click "Generate PDF Archive Summary" to trigger.
                    </div>
                  )}
                </div>
              </div>

              {/* Task Recovery & 7-Day Auto-Purge Vault */}
              <div className="bg-[#030612]/95 border border-indigo-500/20 p-6 rounded-2xl flex flex-col space-y-4 text-left shadow-2xl relative overflow-hidden mt-6">
                <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/[0.015] rounded-full blur-3xl pointer-events-none"></div>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-slate-800 pb-3 gap-3">
                  <div>
                    <h4 className="font-sans font-bold text-sm text-white uppercase tracking-wider flex items-center gap-2">
                      <Archive className="w-4 h-4 text-[#00e5ff]" /> 7-Day Secret Recovery Vault & Completed History ({archivedTasks.length} Archived)
                    </h4>
                    <span className="text-[10px] text-slate-500 font-mono uppercase block mt-0.5">Secure transient buffer storage. Items auto-purge exactly 7 days after entry.</span>
                  </div>
                  <div className="font-mono text-[9px] bg-indigo-950/40 border border-indigo-500/20 text-indigo-400 px-2.5 py-1 rounded">
                    STATUS: RECOVERY BUFFERS STABLE
                  </div>
                </div>

                <div className="space-y-2.5 max-h-[300px] overflow-y-auto custom-scrollbar">
                  {archivedTasks.length > 0 ? (
                    archivedTasks.map((task) => {
                      const expiresAt = (task.deletedAt || Date.now()) + 7 * 24 * 60 * 60 * 1000;
                      const diff = expiresAt - Date.now();
                      const daysLeft = Math.max(0, Math.ceil(diff / (24 * 60 * 60 * 1000)));
                      
                      return (
                        <div 
                          key={task.id} 
                          className="bg-black/40 border border-slate-800/80 p-3.5 rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:border-slate-700 transition-colors"
                        >
                          <div className="flex-1 text-left min-w-0 space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-sans font-black text-xs text-slate-200 truncate">{task.name}</span>
                              <span className="font-mono text-[8px] bg-slate-900 border border-slate-800 px-1 py-0.5 text-slate-500 rounded uppercase">{task.node || "NODE-18.5"}</span>
                              <span className={`font-mono text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider ${
                                task.archivedReason === 'COMPLETED' ? 'bg-emerald-950/40 border border-emerald-500/35 text-emerald-400' : 'bg-rose-950/40 border border-rose-500/35 text-rose-400'
                              }`}>
                                {task.archivedReason || 'DELETED'}
                              </span>
                              
                              {task.tags && task.tags.map((tag: string) => (
                                <span key={tag} className="font-mono text-[8px] px-1.5 py-0.5 rounded border border-indigo-500/25 bg-indigo-500/5 text-indigo-400 uppercase tracking-widest">{tag}</span>
                              ))}
                            </div>
                            <div className="font-mono text-[9px] text-slate-400 flex items-center gap-2 flex-wrap">
                              <span>Origin Dept: <strong className="text-slate-300">{task.category}</strong></span>
                              <span className="text-slate-600">•</span>
                              <span>Progress: <strong className="text-slate-300">{task.progress}%</strong></span>
                            </div>
                          </div>

                          <div className="flex items-center gap-4 shrink-0 justify-between sm:justify-end">
                            <div className="text-right font-mono">
                              <div className="text-[7px] text-slate-500 uppercase">Auto-Purging In</div>
                              <div className={`text-[10px] font-bold ${daysLeft <= 2 ? 'text-rose-400' : daysLeft <= 4 ? 'text-amber-500' : 'text-emerald-400'}`}>
                                {daysLeft} days remaining
                              </div>
                            </div>

                            <button
                              type="button"
                              onClick={() => handleRestoreArchivedTask(task)}
                              className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 border border-indigo-500 text-white rounded text-[9px] font-mono font-black tracking-widest uppercase transition-colors cursor-pointer flex items-center gap-1 shadow-md shadow-indigo-600/15"
                            >
                              <RefreshCw className="w-3 h-3 text-white" /> Restore Core
                            </button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center font-mono text-slate-600 py-8 border border-dashed border-slate-900 rounded-lg bg-black/10 text-[10px]">
                      RESTORE_DAEMON // Archive recovery vault is currently empty.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'NEXORAX_BACKUP_STORAGE' && (
            <div className="p-4 md:p-6 flex flex-col space-y-6 overflow-y-auto h-full custom-scrollbar bg-black/40">
              <div className="flex justify-between items-center border-b border-indigo-500/20 pb-4">
                <div>
                  <h3 className="text-xl font-bold text-white uppercase tracking-wide font-sans flex items-center gap-2">
                    <Database className="w-5 h-5 text-cyan-400 animate-pulse" /> NEXORAX BACKUP STORAGE BLOCKS
                  </h3>
                  <span className="text-slate-400 text-xs font-mono tracking-widest mt-1 block uppercase">NexoraX Alternative (Firebase Eclipse)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] bg-slate-900 border border-slate-700 px-3 py-1.5 rounded text-slate-300 font-mono font-bold uppercase tracking-wider flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block animate-pulse"></span>
                    HOT_RUNNING
                  </span>
                </div>
              </div>
              
              <NexoraXCloudDashboard
                cloudStorageUsed={cloudStorageUsed}
                workspaceFiles={workspaceFiles}
                setCloudStorageUsed={setCloudStorageUsed}
                setWorkspaceFiles={setWorkspaceFiles}
                workspaceCloudLogs={workspaceCloudLogs}
                setWorkspaceCloudLogs={setWorkspaceCloudLogs}
                isSyncingWorkspace={isSyncingWorkspace}
                setIsSyncingWorkspace={setIsSyncingWorkspace}
                defaultModule="STORAGE"
              />
            </div>
          )}

          {activeTab === 'NEXORAX_MAIL_ARRAY' && (
            <div className="p-4 md:p-6 flex flex-col space-y-6 overflow-y-auto h-full custom-scrollbar bg-black/40">
              <div className="flex justify-between items-center border-b border-indigo-500/20 pb-4">
                <div>
                  <h3 className="text-xl font-bold text-white uppercase tracking-wide font-sans flex items-center gap-2">
                    <Mail className="w-5 h-5 text-pink-400 animate-pulse" /> UNIFIED MAIL INGRESS ARRAY
                  </h3>
                  <span className="text-slate-400 text-xs font-mono tracking-widest mt-1 block uppercase">EXCHANGE ECLIPSE // SMTP & IMAP PIPELINE</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] bg-slate-900 border border-slate-700 px-3 py-1.5 rounded text-slate-300 font-mono font-bold uppercase tracking-wider flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-pink-500 inline-block animate-pulse"></span>
                    LIVE_SMTP_SECURE
                  </span>
                </div>
              </div>
              
              <NexoraXCloudDashboard
                cloudStorageUsed={cloudStorageUsed}
                workspaceFiles={workspaceFiles}
                setCloudStorageUsed={setCloudStorageUsed}
                setWorkspaceFiles={setWorkspaceFiles}
                workspaceCloudLogs={workspaceCloudLogs}
                setWorkspaceCloudLogs={setWorkspaceCloudLogs}
                isSyncingWorkspace={isSyncingWorkspace}
                setIsSyncingWorkspace={setIsSyncingWorkspace}
                defaultModule="MAIL"
              />
            </div>
          )}

          {activeTab === 'NEXORAX_DOMAIN_MONITOR' && (
            <div className="p-4 md:p-6 flex flex-col space-y-6 overflow-y-auto h-full custom-scrollbar bg-black/40">
              <div className="flex justify-between items-center border-b border-indigo-500/20 pb-4">
                <div>
                  <h3 className="text-xl font-bold text-white uppercase tracking-wide font-sans flex items-center gap-2">
                    <Globe className="w-5 h-5 text-amber-500 animate-pulse" /> DOMAIN REGISTRY & EDGE TUNNEL MONITOR
                  </h3>
                  <span className="text-slate-400 text-xs font-mono tracking-widest mt-1 block uppercase">CLOUDFLARE ECLIPSE // PROXY & HETZNER EDGE</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] bg-slate-900 border border-slate-700 px-3 py-1.5 rounded text-slate-300 font-mono font-bold uppercase tracking-wider flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block animate-pulse"></span>
                    EDGE_TUNNEL_ESTABLISHED
                  </span>
                </div>
              </div>
              
              <NexoraXCloudDashboard
                cloudStorageUsed={cloudStorageUsed}
                workspaceFiles={workspaceFiles}
                setCloudStorageUsed={setCloudStorageUsed}
                setWorkspaceFiles={setWorkspaceFiles}
                workspaceCloudLogs={workspaceCloudLogs}
                setWorkspaceCloudLogs={setWorkspaceCloudLogs}
                isSyncingWorkspace={isSyncingWorkspace}
                setIsSyncingWorkspace={setIsSyncingWorkspace}
                defaultModule="EDGE"
              />
            </div>
          )}

          {activeTab === 'NEXORAX_METERING_LEDGER' && (
            <div className="p-4 md:p-6 flex flex-col space-y-6 overflow-y-auto h-full custom-scrollbar bg-black/40">
              <div className="flex justify-between items-center border-b border-indigo-500/20 pb-4">
                <div>
                  <h3 className="text-xl font-bold text-white uppercase tracking-wide font-sans flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#00e5ff] animate-pulse" /> NEXUS ACCOUNTING & METERING SYSTEM
                  </h3>
                  <span className="text-slate-400 text-xs font-mono tracking-widest mt-1 block uppercase">API_KEY_ENGINE // Two-layer ledger tracking product use, invoices, and 3% Community Allocation</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] bg-slate-900 border border-slate-700 px-3 py-1.5 rounded text-slate-300 font-mono font-bold uppercase tracking-wider flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 inline-block animate-pulse"></span>
                    METERING_ONLINE
                  </span>
                </div>
              </div>
              
              <NexusAccountingSystem />
            </div>
          )}
        </div>

        {/* Side-Panel Area: Category 86 NexoraX Oracle & Directive Terminal */}
        <div className="lg:col-span-1 flex flex-col gap-6">
          
          {/* NexoraX Oracle side-panel widget */}
          <div className="bg-[#050914]/80 backdrop-blur-md border border-slate-700/50 rounded-2xl flex flex-col shadow-xl overflow-hidden relative">
            
            {/* Oracle Header */}
            <div className="px-4 py-3 border-b border-cyan-500/30 bg-cyan-950/20 font-mono text-[10px] font-bold text-cyan-400 uppercase tracking-widest flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-cyan-400 animate-pulse" />
                <span>CATEGORY 86: THE NEXORAX ORACLE</span>
              </div>
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-ping"></span>
                <span className="text-[8px] text-cyan-500 font-extrabold font-mono uppercase">100 SUB-NODES</span>
              </span>
            </div>

            {/* Oracle Main Panel Content */}
            <div className="p-4 flex flex-col gap-3 flex-1">
              
              {/* Active Grounded Sub-Node Selector / Carousel */}
              <div className="bg-black/60 border border-slate-800 p-2.5 rounded-lg flex items-center justify-between text-left font-mono text-[9px]">
                <div className="truncate pr-2">
                  <div className="text-slate-500 text-[7px] uppercase tracking-widest font-black">ACTIVE TELEMETRY GROUNDING</div>
                  <div className="text-cyan-300 font-bold flex items-center gap-1 mt-0.5 truncate">
                    <Cpu className="w-3 h-3 shrink-0 text-cyan-400" />
                    <span>{ORACLE_SUB_NODES[oracleActiveNodeIndex]}</span>
                  </div>
                </div>
                <button 
                  onClick={() => setOracleActiveNodeIndex(prev => (prev + 1) % ORACLE_SUB_NODES.length)}
                  className="px-2 py-1 bg-cyan-950/50 hover:bg-cyan-900 border border-cyan-500/30 text-cyan-400 text-[8px] font-black uppercase tracking-wider rounded transition-colors cursor-pointer shrink-0"
                  title="Cycle Grounding Nodes"
                >
                  Cycle Node
                </button>
              </div>

              {/* Predictive Trends List */}
              <div className="space-y-1.5 max-h-[160px] overflow-y-auto custom-scrollbar">
                {oracleTrends.map((trend) => {
                  const isSelected = selectedTrendId === trend.id;
                  return (
                    <div 
                      key={trend.id}
                      onClick={() => setSelectedTrendId(trend.id)}
                      className={`p-2 rounded-lg border text-left cursor-pointer transition-all ${
                        isSelected 
                          ? 'bg-cyan-950/20 border-cyan-500/50 shadow-[0_0_10px_rgba(34,211,238,0.15)]'
                          : 'bg-black/30 border-slate-800 hover:border-slate-700'
                      }`}
                    >
                      <div className="flex justify-between items-center mb-0.5">
                        <span className="font-sans font-black text-[10px] text-white tracking-wide">{trend.name}</span>
                        <div className="flex items-center gap-1 font-mono text-[8px] font-bold">
                          <Activity className="w-2.5 h-2.5 text-cyan-400" />
                          <span className="text-cyan-400">{trend.probability}</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-end">
                        <span className="font-mono text-[9px] font-bold text-slate-300 truncate max-w-[130px]">{trend.metric}</span>
                        <span className="font-mono text-[7px] text-slate-500 uppercase tracking-wider font-extrabold">{trend.timeline}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Detail view of selected trend */}
              {selectedTrendId && (
                <div className="bg-slate-950/60 border border-slate-800/80 p-2.5 rounded-lg text-left font-mono text-[8px] space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 uppercase tracking-widest font-black">ORACLE IMPACT SHIELD</span>
                    <span className="text-emerald-400 font-bold text-[7px] border border-emerald-500/30 bg-emerald-950/30 px-1 rounded">ACTIVE</span>
                  </div>
                  <div className="text-slate-300 font-medium">Impact Focus: <span className="text-cyan-400 font-bold">{oracleTrends.find(t => t.id === selectedTrendId)?.impact}</span></div>
                  <div className="text-slate-400 pt-0.5 border-t border-slate-900/50">Status Code: <span className="text-slate-300 font-bold">{oracleTrends.find(t => t.id === selectedTrendId)?.status}</span></div>
                </div>
              )}

              {/* Synthesize Button */}
              <button 
                onClick={runNexoraXOracleSynthesis}
                disabled={oracleSynthesizing}
                className="w-full mt-1.5 py-2.5 bg-gradient-to-r from-cyan-600/90 to-blue-700/90 hover:from-cyan-500 hover:to-blue-600 border border-cyan-400/40 text-white font-bold font-mono text-[9px] tracking-widest uppercase rounded-lg transition-all shadow-md shadow-cyan-600/10 flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {oracleSynthesizing ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>SYNTHESIZING...</span>
                  </>
                ) : (
                  <>
                    <Globe className="w-3.5 h-3.5 text-cyan-300 animate-pulse" />
                    <span>SYNTHESIZE CROSS-NODE DATA</span>
                  </>
                )}
              </button>

            </div>
          </div>

          {/* Original Directive Terminal in the side panel */}
          <div className="bg-[#050914]/80 backdrop-blur-md border border-slate-700/50 rounded-2xl flex flex-col h-full max-h-[400px]">
            <div className="px-4 py-3 border-b border-indigo-500/30 bg-indigo-950/30 font-mono text-[10px] font-bold text-indigo-400 uppercase tracking-widest flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Terminal className="w-4 h-4 animate-pulse text-[#00e5ff]" />
                <span>DIRECTIVE TERMINAL</span>
              </div>
              <div className="flex items-center gap-1.5 text-[8px] text-[#00e5ff] bg-[#00e5ff]/10 border border-[#00e5ff]/30 px-2 py-0.5 rounded animate-pulse">
                <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-ping"></span>
                <span>0.8s LATENCY_STANDARD OK</span>
              </div>
            </div>

            {/* Quick Access Hotkeys */}
            <div className="px-4 py-2 border-b border-slate-800 bg-slate-950/40 flex items-center gap-2 flex-wrap">
              <span className="text-[8px] font-mono font-bold text-slate-400 uppercase tracking-wider">HOTKEYS:</span>
              <button
                 type="button"
                 onClick={() => {
                   setLog(prev => [{
                     timestamp: new Date().toLocaleTimeString(),
                     msg: `HOTKEY TRIGGERED: Autonomous redirect to Category 81 (Hermes Acquisition Grid).`
                   }, ...prev]);
                   if (onNavigate) {
                     onNavigate("Category 81: HERMES ACQUISITION GRID");
                   }
                 }}
                 className="px-2.5 py-1 bg-emerald-950/60 hover:bg-emerald-900 border border-emerald-500/30 text-emerald-400 rounded text-[9px] font-mono font-bold tracking-widest uppercase transition-all shadow-[0_0_10px_rgba(16,185,129,0.1)] hover:shadow-[0_0_15px_rgba(16,185,129,0.25)] flex items-center gap-1 cursor-pointer"
              >
                 <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
                 NODE-81 LINK
              </button>
              <button
                 type="button"
                 onClick={() => {
                   setLog(prev => [{
                     timestamp: new Date().toLocaleTimeString(),
                     msg: `HOTKEY TRIGGERED: Autonomous redirect to Category 82 (Transcendental NexoraX Core).`
                   }, ...prev]);
                   if (onNavigate) {
                     onNavigate("Category 82: THE TRANRESCENDENTAL NEXORAX CORE");
                   }
                 }}
                 className="px-2.5 py-1 bg-indigo-950/60 hover:bg-indigo-900 border border-indigo-500/30 text-indigo-400 rounded text-[9px] font-mono font-bold tracking-widest uppercase transition-all shadow-[0_0_10px_rgba(99,102,241,0.1)] hover:shadow-[0_0_15px_rgba(99,102,241,0.25)] flex items-center gap-1 cursor-pointer"
              >
                 <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-pulse"></span>
                 NODE-82 LINK
              </button>
              <button
                 type="button"
                 onClick={() => {
                   setLog(prev => [{
                     timestamp: new Date().toLocaleTimeString(),
                     msg: `HOTKEY TRIGGERED: Autonomous redirect to Category 83 (Omega Lock).`
                   }, ...prev]);
                   if (onNavigate) {
                     onNavigate("Category 83: THE OMEGA LOCK");
                   }
                 }}
                 className="px-2.5 py-1 bg-rose-950/60 hover:bg-rose-900 border border-rose-500/30 text-rose-400 rounded text-[9px] font-mono font-bold tracking-widest uppercase transition-all shadow-[0_0_10px_rgba(244,63,94,0.1)] hover:shadow-[0_0_15px_rgba(244,63,94,0.25)] flex items-center gap-1 cursor-pointer"
              >
                 <span className="w-1.5 h-1.5 bg-rose-400 rounded-full animate-pulse"></span>
                 NODE-83 LINK
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-3 font-mono text-[10px] text-slate-300 custom-scrollbar">
              {log.length === 0 && (
                 <div className="text-slate-600 italic">No directives issued... Awaiting Director input. (Try "Status Update")</div>
              )}
              {log.map((entry, idx) => (
                 <div key={idx} className="flex flex-col gap-1 border-b border-slate-800/50 pb-2">
                   <span className="text-[9px] text-indigo-500/70">{entry.timestamp}</span>
                   <span className={entry.msg.includes('COMMAND') ? 'text-white font-bold' : entry.msg.includes('ACTION') || entry.msg.includes('SUCCESS') ? 'text-emerald-400' : 'text-cyan-400'}>{entry.msg}</span>
                 </div>
              ))}
            </div>

            <form onSubmit={handleCommand} className="p-3 border-t border-slate-800 bg-black/60">
              <div className="relative">
                <input 
                  type="text" 
                  value={statusInput}
                  onChange={e => setStatusInput(e.target.value)}
                  placeholder="INPUT MANDATE..." 
                  className="w-full bg-[#0b0f19] border border-slate-700 rounded px-4 py-3 pr-10 outline-none focus:border-indigo-500 text-white font-mono text-xs uppercase"
                />
                <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-indigo-400">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
